import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import axios from 'axios';
import MediaPicker from '../components/MediaPicker';
import StickerImage from '../components/StickerImage';
import ChatImage from '../components/ChatImage';
import { isStickerMessage } from '../utils/stickers';
import { renderMessageText } from '../utils/messageFormatting';
import StickersPanel from '../components/StickersPanel';
import SavedTextsPanel from '../components/SavedTextsPanel';
import SavedPhotosPanel from '../components/SavedPhotosPanel';
import VoiceMessage from '../components/VoiceMessage';
import ReadReceipts from '../components/ReadReceipts';
import TypingIndicator from '../components/TypingIndicator';
import OnlineStatus from '../components/OnlineStatus';
import { onVisibilityFetch } from '../utils/pollingKeepAlive';
import { sortMessages } from '../utils/messageOrder';
import WhatsAppInput from '../components/WhatsAppInput';
import HandlerProfile from './HandlerProfile';
import AudioCallScreen from '../components/AudioCallScreen';
import EditClientModal from '../components/EditClientModal';
import MBSetupBadge, { MB_SETUPS } from '../components/MBSetupBadge';
import MultiSelectForwardModal from '../components/MultiSelectForwardModal';
import CancelRemarkModal from '../components/CancelRemarkModal';
import {
  connectSocket,
  disconnectSocket,
  joinRoom,
  leaveRoom,
  sendMessage as sendSocketMessage,
  sendTypingIndicator,
  markMessagesRead,
  onNewMessage,
  onUserTyping,
  onUserOnline,
  onUserOffline,
  onMessagesRead,
  onMessagesDelivered,
  offNewMessage,
  offUserTyping,
  offUserOnline,
  offUserOffline,
  offMessagesRead,
  offMessagesDelivered,
  onMessageEdited,
  offMessageEdited,
  onMessageDeleted,
  offMessageDeleted,
  onCustomerRegistered,
  offCustomerRegistered
} from '../utils/socket';
import socket from '../utils/socket';
import { 
  ArrowLeft,
  UserCircle,
  PaperPlaneRight,
  DotsThreeVertical,
  Trash,
  Copy,
  ShareFat,
  Smiley,
  Plus,
  Prohibit,
  ArrowBendUpLeft,
  X,
  FileText,
  CaretDown,
  Phone,
  WhatsappLogo,
  Star,
  ArrowsLeftRight,
  Check,
  Checks,
  Flag,
  PencilSimple,
  CircleNotch
} from '@phosphor-icons/react';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExts = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
  const lower = url.split('?')[0].toLowerCase();
  return videoExts.some(ext => lower.endsWith(ext));
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(typeof ts === 'number' ? ts : ts);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const buildMediaUrl = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  if (cleanUrl.startsWith('/api/')) return `${BACKEND_URL}${cleanUrl}`;
  if (cleanUrl.startsWith('/uploads/')) return `${BACKEND_URL}${cleanUrl}`;
  return `${API}${cleanUrl}`;
};

// Date separator helpers
const getDateLabel = (timestamp) => {
  if (!timestamp) return '';
  const msgDate = new Date(timestamp);
  if (isNaN(msgDate.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (msgDate.toDateString() === today.toDateString()) return 'Today';
  if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return msgDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const shouldShowDateSeparator = (msgs, idx) => {
  if (idx === 0) return true;
  const prev = new Date(msgs[idx - 1].timestamp);
  const curr = new Date(msgs[idx].timestamp);
  if (isNaN(prev.getTime()) || isNaN(curr.getTime())) return false;
  return prev.toDateString() !== curr.toDateString();
};

// Helper to detect consecutive image groups from the same sender
const getImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (isStickerMessage(msg) || !msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url)) return null;
  if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return null;
  if (idx > 0) {
    const prev = msgs[idx - 1];
    if (!isStickerMessage(prev) && prev.image_url && !prev.voice_url && !prev.document_url && !isVideoUrl(prev.image_url) && prev.sender_id === msg.sender_id && !prev.caption && (!prev.text || !prev.text.trim() || prev.text === '🖼️ Photo' || prev.text === '🎥 Video')) return null;
  }
  const group = [msg];
  for (let i = idx + 1; i < msgs.length; i++) {
    if (group.length >= 10) break;
    const nextMsg = msgs[i];
    if (isStickerMessage(nextMsg) || !nextMsg.image_url || nextMsg.voice_url || nextMsg.document_url || isVideoUrl(nextMsg.image_url) || nextMsg.sender_id !== msg.sender_id) break;
    const hasCaption = nextMsg.caption || (nextMsg.text && nextMsg.text.trim() && nextMsg.text !== '🖼️ Photo' && nextMsg.text !== '🎥 Video');
    if (!hasCaption) {
      group.push(nextMsg);
    } else {
      group.push(nextMsg);
      break;
    }
  }
  return group.length > 1 ? group : null;
};

const isPartOfImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (isStickerMessage(msg) || !msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || idx === 0) return false;
  let groupCount = 0;
  for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
    const prevMsg = msgs[i];
    if (!isStickerMessage(prevMsg) && prevMsg.image_url && !prevMsg.voice_url && !prevMsg.document_url && !isVideoUrl(prevMsg.image_url) && prevMsg.sender_id === msg.sender_id && !prevMsg.caption && (!prevMsg.text || !prevMsg.text.trim() || prevMsg.text === '🖼️ Photo' || prevMsg.text === '🎥 Video')) {
      groupCount++;
    } else break;
  }
  return groupCount > 0 && groupCount < 10;
};

const HandlerChat = ({ embeddedRoomId, onBackToList, onOpenProfile }) => {
  const params = useParams();
  const roomId = embeddedRoomId || params.roomId;
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification, playNotificationSound, requestPermission } = useWebNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;
  
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false); // Changed to false for instant loading
  const [showProfile, setShowProfile] = useState(false);
  const [showProfileDrawer, setShowProfileDrawer] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showMBMenu, setShowMBMenu] = useState(false);
  const [longPressMenu, setLongPressMenu] = useState(null); // {messageId, x, y}
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null); // messageId
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showStickersPanel, setShowStickersPanel] = useState(false);
  const [showSavedTextsPanel, setShowSavedTextsPanel] = useState(false);
  const [showSavedPhotosPanel, setShowSavedPhotosPanel] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editClientData, setEditClientData] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showPendingRequestsModal, setShowPendingRequestsModal] = useState(false);
  const [selectedPendingRequest, setSelectedPendingRequest] = useState(null);
  const [showCreateDepositFormModal, setShowCreateDepositFormModal] = useState(false);
  const [isCreateAndApproveFlow, setIsCreateAndApproveFlow] = useState(false);
  const [createDepositSubmitting, setCreateDepositSubmitting] = useState(false);
  const [showCreateWithdrawFormModal, setShowCreateWithdrawFormModal] = useState(false);
  const [showPendingWithdrawRequestsModal, setShowPendingWithdrawRequestsModal] = useState(false);
  const [showCoinCutModal, setShowCoinCutModal] = useState(false);
  const [editingCoinCutMsg, setEditingCoinCutMsg] = useState(null);
  const [showEditWithdrawModal, setShowEditWithdrawModal] = useState(false);
  const [editingWithdrawMsg, setEditingWithdrawMsg] = useState(null);
  const [editingDepositMsg, setEditingDepositMsg] = useState(null);
  const [cancellingDepositId, setCancellingDepositId] = useState(null);
  const [cancellingMsg, setCancellingMsg] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [registerForm, setRegisterForm] = useState({ name: '', phone: '', password: 'Abcd1234' });
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  
  // Reply & Forward features
  const [replyingTo, setReplyingTo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [availableChats, setAvailableChats] = useState([]);
  const [messageToForward, setMessageToForward] = useState(null);
  
  // Phase 2: Advanced features
  const [customerTyping, setCustomerTyping] = useState(false);
  const [customerOnlineStatus, setCustomerOnlineStatus] = useState({ is_online: false, last_seen: null });
  const [typingTimeout, setTypingTimeout] = useState(null);
  const typingCheckRef = useRef(null);
  const markedReadIdsRef = useRef(new Set());
  const customerRef = useRef(customer);
  customerRef.current = customer;
  const loadCustomerRef = useRef(null);

  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [allMediaUrls, setAllMediaUrls] = useState([]);
  const [currentMediaIdx, setCurrentMediaIdx] = useState(0);

  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const menuRef = useRef(null);
  const longPressTimer = useRef(null);
  const [inputKey, setInputKey] = useState(0);
  const chatContainerRef = useRef(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [showForwardGroupModal, setShowForwardGroupModal] = useState(false);
  const [forwardGroupMsg, setForwardGroupMsg] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [editingMsg, setEditingMsg] = useState(null);
  const [editText, setEditText] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const containerRef = useRef(null);

  // Scroll-up pagination state
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const isPrependingRef = useRef(false);
  const reachedTopRef = useRef(false);
  const isLoadingOlderRef = useRef(false);
  const prevScrollHeightRef = useRef(null);
  const prevScrollTopRef = useRef(null);
  const prependedCountRef = useRef(0);
  const messagesRef = useRef([]);
  const historyRequestIdRef = useRef(0);
  const historyAbortRef = useRef(null);
  const activeRoomIdRef = useRef(roomId);

  useEffect(() => {
    activeRoomIdRef.current = roomId;
  }, [roomId]);


  // Visual Viewport Keyboard Fix for PWA
  useEffect(() => {
    if (embeddedRoomId) return;
    const handleViewportResize = () => {
      if (containerRef.current && window.visualViewport) {
        const vh = window.visualViewport.height;
        containerRef.current.style.height = `${vh}px`;
      }
    };
    
    if (window.visualViewport) {
      handleViewportResize();
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, [embeddedRoomId]);

  // Listen for incoming calls
  useEffect(() => {
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user.id) {
        setIncomingCall({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerProfilePic: data.caller_profile_pic,
          isConference: data.is_conference || false,
          participantNames: data.participant_names || []
        });
      }
    };
    socket.on('incoming_call', handleIncomingCall);
    return () => socket.off('incoming_call', handleIncomingCall);
  }, [user]);
  
  // Track which messages have already played notification sound
  const notifiedMessageIds = useRef(new Set());
  
  // Ref to track previous messages count for notification logic
  const prevMessagesCountRef = useRef(0);
  // Flag to skip notification on initial load
  const isInitialLoadRef = useRef(true);

  // Reset scroll-up pagination when switching chats
  useEffect(() => {
    setMessages([]);
    messagesRef.current = [];
    prevMsgLenRef.current = 0;
    prevMessagesCountRef.current = 0;
    historyRequestIdRef.current += 1;
    historyAbortRef.current?.abort();
    setHasMoreOlder(true);
    setIsLoadingOlder(false);
    isLoadingOlderRef.current = false;
    isPrependingRef.current = false;
    reachedTopRef.current = false;
    prependedCountRef.current = 0;
    prevScrollHeightRef.current = null;
    prevScrollTopRef.current = null;
  }, [roomId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);


  const EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];


  // Register customer from chat
  const handleRegisterCustomer = async () => {
    if (!registerForm.name || !registerForm.phone) {
      toast.error('Name and mobile number are required');
      return;
    }
    if (registerForm.phone.length !== 10) {
      toast.error('Mobile number must be 10 digits');
      return;
    }
    
    setRegistering(true);
    try {
      await axios.post(`${API}/inquiry/register-customer`, {
        guest_user_id: customer?.id || '',
        name: registerForm.name,
        phone: registerForm.phone,
        password: registerForm.password || 'Abcd1234',
        room_id: roomId,
        registered_by: user.id,
        login_link: `${window.location.origin}/customer-login`
      });
      
      toast.success('Customer registered successfully!');
      setShowRegisterModal(false);
      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
      setCustomer(prev => (prev ? {
        ...prev,
        name: registerForm.name,
        phone: registerForm.phone,
        is_guest: false
      } : prev));
      // Reload messages to show the auto-sent credentials
      loadMessages();
      loadCustomer();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to register customer';
      toast.error(msg);
    }
    setRegistering(false);
  };



  // Stable loadMessages function - no dependencies that change frequently
  const loadMessages = useCallback(async () => {
    if (!roomId || !user?.id) return;

    const requestId = ++historyRequestIdRef.current;
    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;
    
    try {
      const response = await axios.get(`${API}/chat/messages/${roomId}`, {
        params: { user_id: user.id, limit: 25 },
        signal: controller.signal
      });

      if (requestId !== historyRequestIdRef.current || activeRoomIdRef.current !== roomId) return;
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`✅ Handler loaded ${response.data.length} messages for room ${roomId}`);
        
        // Skip notifications on initial load
        if (isInitialLoadRef.current) {
          console.log('📥 Handler: Initial load - skipping notifications');
          isInitialLoadRef.current = false;
          setHasMoreOlder(response.data.length >= 25);
          if (response.data.length < 25) {
            reachedTopRef.current = true;
          }
          prevMessagesCountRef.current = response.data.length;
          const initFiltered = response.data.filter((m) => {
            if (m?.deleted_by_admin || m?.deletedByAdmin) return false;
            if (m?.bot_type !== 'handoff_summary') return true;
            try { return (Date.now() - new Date(m.timestamp).getTime()) / 3600000 < 24; } catch { return true; }
          });
          setMessages(prev => {
            if (!prev || prev.length === 0) return sortMessages(initFiltered);
            const map = new Map();
            prev.forEach(m => { if (m && m.id) map.set(m.id, m); });
            initFiltered.forEach(m => {
              if (m && m.id) {
                const existing = map.get(m.id);
                map.set(m.id, { ...existing, ...m });
              }
            });
            return sortMessages(Array.from(map.values()));
          });
          // Continue to mark messages as read below
        } else {
          if (prevMessagesCountRef.current === 0) {
            setHasMoreOlder(response.data.length >= 25);
            if (response.data.length < 25) {
              reachedTopRef.current = true;
            }
          } else if (!reachedTopRef.current) {
            setHasMoreOlder(true);
          }

          // Check for new messages from customer and show notification
          const prevMessagesLength = prevMessagesCountRef.current;
          const newMessages = response.data;
        
        if (prevMessagesLength > 0 && newMessages.length > prevMessagesLength) {
          // Get only the NEW messages
          const newlyAddedMessages = newMessages.slice(prevMessagesLength);
          
          newlyAddedMessages.forEach((newMsg) => {
            // Notify if message is from customer AND we haven't notified for this message yet
            if (newMsg.sender_id !== user.id && 
                newMsg.sender_role === 'customer' &&
                !notifiedMessageIds.current.has(newMsg.id)) {
              
              console.log('🔔 Handler: New message from customer, showing notification for message:', newMsg.id);
              
              // Mark as notified
              notifiedMessageIds.current.add(newMsg.id);
              
              const messagePreview = newMsg.message_type === 'text' 
                ? newMsg.message_text 
                : newMsg.message_type === 'image' 
                ? '📷 Photo' 
                : newMsg.message_type === 'video'
                ? '🎥 Video'
                : newMsg.message_type === 'voice'
                ? '🎤 Voice message'
                : 'New message';
              
              showNotificationRef.current({
                title: `${customer?.name || 'Customer'} - DWS Vault Chat`,
                body: messagePreview,
                tag: `chat-${roomId}`,
                data: {
                  roomId: roomId,
                  senderId: newMsg.sender_id,
                  messageType: newMsg.message_type
                }
              });
            }
          });
        }
        
        // Update ref for next comparison
        prevMessagesCountRef.current = newMessages.length;
        // Auto-hide handoff_summary messages older than 24 hours
        const _filtered = response.data.filter((m) => {
          if (m?.deleted_by_admin || m?.deletedByAdmin) return false;
          if (m?.bot_type !== 'handoff_summary') return true;
          try { return (Date.now() - new Date(m.timestamp).getTime()) / 3600000 < 24; } catch { return true; }
        });
        setMessages(prev => {
          if (!prev || prev.length === 0) return sortMessages(_filtered);
          const map = new Map();
          prev.forEach(m => {
            if (m && m.id) map.set(m.id, m);
          });
          _filtered.forEach(m => {
            if (m && m.id) {
              const existing = map.get(m.id);
              map.set(m.id, { ...existing, ...m });
            }
          });
          return sortMessages(Array.from(map.values()));
        });
        } // End of else block for non-initial load
        
        // Mark only RECEIVED messages as read for current user (not own sent messages)
        const unreadMessageIds = response.data
          .filter(msg => {
            if (!msg.id || msg.id === 'undefined' || String(msg.id).startsWith('temp_')) return false;
            const senderId = msg.sender_id || msg.senderId;
            if (!senderId || senderId === user.id) return false;
            const readBy = msg.readBy || msg.read_by || {};
            return !readBy[user.id];
          })
          .map(msg => msg.id);
        
        // Always dispatch the event so the badge clears immediately when chat is opened,
        // even if there are 0 newly-unread messages (backend may already count 0 but
        // frontend still shows a stale badge from a previous load).
        try {
          if (unreadMessageIds.length > 0) {
            markMessagesRead(roomId, unreadMessageIds, user.id);
            await axios.post(`${API}/messages/mark-read`, {
              room_id: roomId,
              message_ids: unreadMessageIds
            }, {
              params: { user_id: user.id }
            });
            console.log(`✅ Marked ${unreadMessageIds.length} messages as read`);
          }
          // Always fire so HandlerDashboard clears the stale badge
          window.dispatchEvent(new CustomEvent('messages-marked-read', { detail: { roomId, userId: user.id } }));
        } catch (error) {
          console.error('Failed to mark messages as read:', error);
          // Still clear the badge on the frontend even if API call failed
          window.dispatchEvent(new CustomEvent('messages-marked-read', { detail: { roomId, userId: user.id } }));
        }
      }
      setLoading(false);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Failed to load messages:', error);
      setLoading(false);
      // Don't clear existing messages on error
    } finally {
      if (historyAbortRef.current === controller) historyAbortRef.current = null;
    }
  }, [roomId, user?.id, customer?.name]); // showNotification via ref

  // Fetch older messages when user scrolls to top (WhatsApp-style pagination)
  const fetchOlderMessages = useCallback(async () => {
    if (isLoadingOlderRef.current || !hasMoreOlder || !roomId || !user?.id) return;

    const currentMsgs = messagesRef.current;
    if (!currentMsgs || currentMsgs.length === 0) {
      setHasMoreOlder(false);
      return;
    }

    // Find the oldest valid non-temporary message with a timestamp
    let oldest = null;
    let oldestTimestamp = null;
    for (let i = 0; i < currentMsgs.length; i++) {
      const m = currentMsgs[i];
      if (!m || m.is_internal || String(m.id).startsWith('temp_')) continue;
      const raw = m.createdAt ?? m.created_at ?? m.timestamp ?? m.updatedAt ?? m.updated_at;
      const ts = typeof raw === 'number' ? raw : (raw ? new Date(raw).getTime() : null);
      if (ts && !Number.isNaN(ts) && ts > 0) {
        oldest = m;
        oldestTimestamp = ts;
        break;
      }
    }

    if (!oldest || !oldestTimestamp) {
      setHasMoreOlder(false);
      return;
    }

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);

    if (chatContainerRef.current) {
      prevScrollHeightRef.current = chatContainerRef.current.scrollHeight;
      prevScrollTopRef.current = chatContainerRef.current.scrollTop;
    }

    try {
      const response = await axios.get(`${API}/chat/messages/${roomId}`, {
        params: { user_id: user.id, limit: 25, before: oldestTimestamp, beforeId: oldest.id }
      });
      if (activeRoomIdRef.current !== roomId) return;
      if (response.data && Array.isArray(response.data)) {
        const batch = response.data;
        if (batch.length < 25) {
          setHasMoreOlder(false);
          reachedTopRef.current = true;
        }
        if (batch.length > 0) {
          isPrependingRef.current = true;
          prependedCountRef.current += 1;
          setMessages(current => {
            const existingIds = new Set(current.map(m => m.id).filter(Boolean));
            const existingTempIds = new Set(current.map(m => m.temp_id || m.tempId).filter(Boolean));
            const newBatch = batch.filter(m => {
              if (m.deleted_by_admin || m.deletedByAdmin) return false;
              if (m.id && existingIds.has(m.id)) return false;
              const tId = m.temp_id || m.tempId;
              if (tId && existingTempIds.has(tId)) return false;
              return true;
            });
            if (newBatch.length > 0) {
              return sortMessages([...newBatch, ...current]);
            }
            return current;
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch older messages:', err);
    } finally {
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [roomId, user?.id, hasMoreOlder]);


  // After prepend, restore scroll position so user stays at same visual position
  useLayoutEffect(() => {
    if (prependedCountRef.current > 0 && prevScrollHeightRef.current !== null && chatContainerRef.current) {
      const newScrollHeight = chatContainerRef.current.scrollHeight;
      const heightDiff = newScrollHeight - prevScrollHeightRef.current;
      chatContainerRef.current.scrollTop = (prevScrollTopRef.current || 0) + heightDiff;
      prevScrollHeightRef.current = null;
      prevScrollTopRef.current = null;
    }
  }, [isLoadingOlder]);

  // Handle image preview
  const handleImageClick = (imageUrl) => {

    const mediaList = messages
      .filter(m => m.image_url && !isStickerMessage(m))
      .map(m => buildMediaUrl(m.image_url));
    setAllMediaUrls(mediaList);
    const idx = mediaList.indexOf(imageUrl);
    setCurrentMediaIdx(idx >= 0 ? idx : 0);
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Detect if user is scrolling up to read old messages
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is more than 100px from bottom, they're reading old messages
    setIsUserScrolling(distanceFromBottom > 30);

    // Trigger older message fetch when near the top (WhatsApp-style pagination)
    if (scrollTop < 120) {
      fetchOlderMessages();
    }
  };

  const prevMsgLenRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      if (isPrependingRef.current) {
        isPrependingRef.current = false;
        prevMsgLenRef.current = messages.length;
        return;
      }
      if (prevMsgLenRef.current === 0) {
        scrollToBottom();
        requestAnimationFrame(scrollToBottom);
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 500);
      } else if (messages.length > prevMsgLenRef.current && !isUserScrolling) {
        requestAnimationFrame(scrollToBottom);
      }
      prevMsgLenRef.current = messages.length;
    }
  }, [messages.length, roomId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Store loadMessages in a ref to avoid dependency issues
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  // Load messages - initial load + visibility refresh (Socket.IO handles real-time)
  useEffect(() => {
    // Initial load
    loadMessagesRef.current();
    
    // Instant refresh when tab returns to focus
    const cleanupVisibility = onVisibilityFetch(() => {
      loadMessagesRef.current();
    });
    
    // Cleanup
    return () => {
      cleanupVisibility();
    };
  }, [roomId, user?.id]);

  // Socket.IO real-time messaging (alongside polling for zero-risk)
  useEffect(() => {
    if (!roomId || !user?.id) return;
    
    connectSocket(user.id);
    joinRoom(roomId, user.id);
    console.log('🔌 Socket.IO joined room:', roomId);
    
    const handleCustomerRegistered = (data) => {
      if (!data) return;
      if (data.roomId === roomId || data.room_id === roomId || (customerRef.current?.id && (data.customerId === customerRef.current.id || data.customer_id === customerRef.current.id))) {
        setCustomer(prev => (prev ? {
          ...prev,
          name: data.name || prev.name,
          username: data.name || prev.username,
          phone: data.phone || prev.phone,
          is_guest: false,
          role: 'customer'
        } : prev));
        setShowRegisterModal(false);
        setShowMenu(false);
        if (data.registered_by_id && data.registered_by_id !== user?.id) {
          toast.info(`Customer registered by ${data.registered_by || 'staff'}`);
        }
        loadCustomerRef.current?.();
      }
    };
    
    const handleNewMessage = (data) => {
      if (data.room_id === roomId && data.message) {
        setMessages(prev => {
          // Dedup by message.id - polling may already have this message
          if (prev.some(m => m.id === data.message.id)) return prev;
          const updated = sortMessages([...prev, data.message]);
          if (updated.length >= 25 && !reachedTopRef.current) {
            setHasMoreOlder(true);
          }
          return updated;
        });

        // If message is from customer, mark as read immediately since handler has chat open
        const newMsg = data.message;
        const botType = newMsg.bot_type || newMsg.botType || newMsg.media?.bot_type || newMsg.media?.botType;
        if (botType === 'customer_registered') {
          const regData = newMsg.bot_data || newMsg.botData || newMsg.media?.bot_data || newMsg.media?.botData || {};
          handleCustomerRegistered({
            roomId,
            customerId: regData.customerId || regData.customer_id,
            name: regData.name,
            phone: regData.phone,
            registered_by: regData.registered_by,
            registered_by_id: regData.registered_by_id
          });
        }
        const msgId = newMsg.id || newMsg.message_id;
        const senderId = newMsg.sender_id || newMsg.senderId;
        if (msgId && msgId !== 'undefined' && !String(msgId).startsWith('temp_') && senderId && senderId !== user.id && !markedReadIdsRef.current.has(msgId)) {
          markedReadIdsRef.current.add(msgId);
          markMessagesRead(roomId, [msgId], user.id);
          axios.post(`${API}/messages/mark-read`, {
            room_id: roomId,
            message_ids: [msgId]
          }, {
            params: { user_id: user.id }
          }).catch(() => {});
          window.dispatchEvent(new CustomEvent('messages-marked-read', { detail: { roomId, userId: user.id } }));
        }
      }
    };
    
    // Listen for typing indicators via Socket.IO
    const handleUserTyping = (data) => {
      if (data.room_id === roomId && data.user_id !== user.id) {
        setCustomerTyping(data.is_typing);
      }
    };
    
    // Listen for read receipts (customer read our messages → update ticks)
    const handleMessagesRead = (data) => {
      if (data.room_id === roomId && data.message_ids) {
        setMessages(prev => prev.map(m => 
          data.message_ids.includes(m.id) ? { ...m, delivered: true, read: true } : m
        ));
      }
    };
    
    // Listen for delivered receipts (customer received our messages → double tick)
    const handleMessagesDelivered = (data) => {
      if (data.room_id === roomId && data.message_ids) {
        setMessages(prev => prev.map(m => 
          data.message_ids.includes(m.id) ? { ...m, delivered: true } : m
        ));
      }
    };

    const handleMessageEdited = (data) => {
      if (data.room_id === roomId && data.message) {
        if (data.message.deleted_by_admin || data.message.deletedByAdmin) {
          setMessages(prev => prev.filter(m => m.id !== data.message.id));
        } else {
          setMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
        }
      }
    };

    const handleMessageDeleted = (data) => {
      if (data.room_id === roomId && (data.message_id || data.messageId)) {
        const delId = data.message_id || data.messageId;
        setMessages(prev => prev.filter(m => m.id !== delId));
      }
    };
    
    onNewMessage(handleNewMessage);
    onMessageEdited(handleMessageEdited);
    onMessageDeleted(handleMessageDeleted);
    onCustomerRegistered(handleCustomerRegistered);
    onUserTyping(handleUserTyping);
    onMessagesRead(handleMessagesRead);
    onMessagesDelivered(handleMessagesDelivered);
    
    return () => {
      offNewMessage(handleNewMessage);
      offMessageEdited(handleMessageEdited);
      offMessageDeleted(handleMessageDeleted);
      offCustomerRegistered(handleCustomerRegistered);
      offUserTyping(handleUserTyping);
      offMessagesRead(handleMessagesRead);
      offMessagesDelivered(handleMessagesDelivered);
      leaveRoom(roomId, user.id);
    };
  }, [roomId, user?.id]);

  // Load customer info
  const loadCustomer = useCallback(async () => {
    if (!roomId) return;
    try {
      const roomResponse = await axios.get(`${API}/chat/room/${roomId}`);
      const room = roomResponse.data;
      if (room) {
        setCustomer({
          id: room.customer_id,
          name: room.customer_name,
          phone: room.customer_phone,
          profile_pic: room.customer_profile_pic,
          is_guest: room.customer_is_guest !== undefined ? room.customer_is_guest : (room.customer_phone ? room.customer_phone.startsWith('GUEST_') : false),
          role: room.customer_role || 'customer',
          mb_setup: room.mb_setup || null,
          is_suspicious: room.is_suspicious || false,
          flag_reason: room.flag_reason || '',
          is_vip: room.is_vip || false,
          chat_type: room.chat_type || '',
          is_chat_support: room.chat_support_source === true,
          inquiry_id: room.inquiry_id || null,
          address: room.customer_address || room.address || ''
        });
      }
    } catch (error) {
      console.error('Failed to load customer:', error);
    }
  }, [roomId]);

  loadCustomerRef.current = loadCustomer;

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  // Phase 2: Update online status
  useEffect(() => {
    if (user?.id) {
      console.log('[HandlerChat] Setting handler online:', user.id);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      })
      .then(() => console.log('[HandlerChat] ✅ Online status updated'))
      .catch(err => console.error('[HandlerChat] ❌ Online status update failed:', err));
    }
    
    return () => {
      if (user?.id) {
        console.log('[HandlerChat] Setting handler offline:', user.id);
        axios.post(`${API}/users/online-status?user_id=${user.id}`, {
          is_online: false
        }).catch(err => console.error('[HandlerChat] ❌ Offline status update failed:', err));
      }
    };
  }, [user]);

  // Phase 2: Get customer online status (Ultra-Fast polling for real-time)
  useEffect(() => {
    if (!customer?.id) return;

    const fetchCustomerStatus = async () => {
      try {
        const res = await axios.get(`${API}/users/status/${customer.id}`);
        // Only update state if values actually changed (prevents re-rendering flicker)
        setCustomerOnlineStatus(prev => {
          if (prev.is_online === res.data.is_online && prev.last_seen === res.data.last_seen) return prev;
          return res.data;
        });
      } catch (err) {
        console.error('Get customer status failed:', err);
      }
    };

    fetchCustomerStatus();
    // Poll every 3 seconds (reduced from 300ms to avoid excessive re-renders)
    const interval = setInterval(fetchCustomerStatus, 3000);

    return () => clearInterval(interval);
  }, [customer]);

  // Heartbeat: Update own online status
  useEffect(() => {
    if (!user?.id) return;
    
    // Set online when component mounts (immediate)
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('Online status update failed:', err));
    
    // Heartbeat every 15s — sufficient to keep presence alive without flooding the API
    const heartbeat = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('Heartbeat failed:', err));
    }, 15000); // was 2000ms
    
    // Set offline when component unmounts
    return () => {
      clearInterval(heartbeat);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: false
      }).catch(err => console.error('Offline status update failed:', err));
    };
  }, [user]);

  // Phase 2: Mark messages as read (Real-time with Socket.IO)
  useEffect(() => {
    if (roomId && messages.length > 0 && user?.id) {
      const unreadIds = messages
        .filter(m => {
          if (!m.id || m.id === 'undefined' || String(m.id).startsWith('temp_') || m.pending) return false;
          if (markedReadIdsRef.current.has(m.id)) return false;
          const senderId = m.sender_id || m.senderId;
          if (!senderId || senderId === user.id) return false;
          const readBy = m.readBy || m.read_by || {};
          return !readBy[user.id];
        })
        .map(m => m.id);
      
      if (unreadIds.length > 0) {
        unreadIds.forEach(id => markedReadIdsRef.current.add(id));
        console.log('[HandlerChat] Marking messages as read:', unreadIds.length);
        
        // Socket.IO real-time read receipt
        markMessagesRead(roomId, unreadIds, user.id);
        
        // HTTP API fallback
        axios.post(`${API}/messages/mark-read`, {
          room_id: roomId,
          message_ids: unreadIds
        }, {
          params: { user_id: user.id }
        })
        .then(() => {
          console.log('[HandlerChat] ✅ Messages marked as read');
          window.dispatchEvent(new CustomEvent('messages-marked-read', { detail: { roomId, userId: user.id } }));
        })
        .catch(err => console.error('[HandlerChat] ❌ Mark read failed:', err));

        // Optimistically set readBy in local messages state
        setMessages(prev => prev.map(msg => {
          if (unreadIds.includes(msg.id)) {
            const updatedReadBy = { ...(msg.read_by || msg.readBy || {}), [user.id]: true };
            return { ...msg, read: true, readBy: updatedReadBy, read_by: updatedReadBy };
          }
          return msg;
        }));
      }
    }
  }, [messages, roomId, user]);

  // Phase 2: Check customer typing status (Fast polling)
  useEffect(() => {
    if (!roomId || !user?.id) return;

    const checkTyping = async () => {
      try {
        const res = await axios.get(`${API}/chat/typing/${roomId}?user_id=${user.id}`);
        setCustomerTyping(res.data.typing_users.length > 0);
      } catch (err) {
        console.error('Typing check failed:', err);
      }
    };

    // Typing poll every 3s — fast enough to feel real, far fewer requests than 500ms
    typingCheckRef.current = setInterval(checkTyping, 3000);

    return () => {
      if (typingCheckRef.current) {
        clearInterval(typingCheckRef.current);
      }
    };
  }, [roomId, user]);

  const sendMessage = async (e, textFromInput = null, options = {}) => {
    e?.preventDefault();
    
    // Use text from parameter (WhatsAppInput) or from inputRef (form submission)
    const messageText = textFromInput || inputRef.current?.value?.trim();
    
    if (!messageText || !roomId) {
      return;
    }

    // IMMEDIATELY clear input (before API call)
    // Note: WhatsAppInput handles its own clearing via handleSend

    try {
      // Prepare message data
      const messageData = {
        room_id: roomId,
        sender_id: user.id,
        text: messageText,
        image_url: null,
        voice_url: null
      };

      // Include any options passed (e.g. message_type, transaction details)
      if (options) {
        Object.assign(messageData, options);
      }

      // Add reply reference if replying to a message
      if (replyingTo) {
        messageData.reply_to = {
          message_id: replyingTo.id,
          text: replyingTo.text || '(Media)',
          sender_name: replyingTo.sender_name
        };
      }

      const response = await axios.post(`${API}/chat/send-message`, messageData);

      // Add message to local state (dedup: Socket.IO server emit may arrive first)
      notifiedMessageIds.current.add(response.data.id);
      setMessages(prev => {
        if (prev.some(m => m.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setIsUserScrolling(false); // Scroll to bottom after sending
      setReplyingTo(null); // Clear reply state
      
      console.log('✅ Handler message sent:', messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || error.response?.data?.error || 'Failed to send message');
      throw error;
    } finally {
      // Phase 2: Stop typing indicator (Socket.IO + HTTP)
      sendTypingIndicator(roomId, user.id, false);
      if (typingTimeout) clearTimeout(typingTimeout);
      axios.post(`${API}/chat/typing?user_id=${user.id}`, {
        room_id: roomId,
        is_typing: false
      }).catch(err => console.error('Typing update failed:', err));
    }
  };

  const handleCreateWithdrawFormClick = async () => {
    if (!roomId) return;
    try {
      const countRes = await axios.get(`${API}/chat/room/${roomId}/pending-count?type=withdrawal`);
      const pendingCount = countRes.data.count;
      const cooldownMins = countRes.data.cooldown_minutes || 0;
      if (pendingCount >= 1) {
        toast.error("You already have 1 open withdraw request. Wait for it to close first.");
        return;
      }
      if (cooldownMins > 0) {
        toast.error("Please wait 30 min for request new Withdrawal");
        return;
      }
      setShowCreateWithdrawFormModal(true);
    } catch (err) {
      console.error(err);
      setShowCreateWithdrawFormModal(true);
    }
  };

  const handleSendCreateWithdrawForm = async (amountVal, refVal) => {
    try {
      const res = await axios.post(`${API}/chat/form-request/create`, {
        type: 'withdrawal',
        amount: parseFloat(amountVal),
        roomId: roomId
      });
      const formRequest = res.data;

      // Fetch saved bank details for account line
      let acctLine = '';
      let bankHolder = '';
      let bankIfsc = '';
      let bankUpi = '';
      try {
        const bankRes = await axios.get(`${API}/client-bank-details/${customer.id}`);
        const bank = bankRes.data?.bank_details || bankRes.data?.client_bank_detail || bankRes.data?.saved_bank_detail;
        if (bank) {
          if (bank.bank_account_number) {
            acctLine = `${bank.bank_name || 'Bank'} · A/c ${bank.bank_account_number}`;
          } else if (bank.upi_id) {
            acctLine = `UPI: ${bank.upi_id}`;
          }
          bankHolder = bank.account_holder_name || '';
          bankIfsc = bank.ifsc_code || '';
          bankUpi = bank.upi_id || '';
        }
      } catch (e) {
        console.error("Could not fetch bank details for card", e);
      }

      if (!acctLine && refVal && refVal.trim()) {
        acctLine = refVal.trim();
      }
      if (!acctLine) {
        acctLine = 'Not Provided';
      }

      const roleLabel = user?.role || 'handler';
      const staffName = user?.username || user?.name || 'handler';
      const senderLabel = `${staffName} (${roleLabel})`;

      const clientUsername = customer?.username || customer?.name || 'client';
      const clientLabel = `${clientUsername} (@${clientUsername})`;
      const cardMsg = `🎫 ${formRequest.form_id || 'W001'} — Withdraw Form\nRequested by: ${senderLabel}\nClient: ${clientLabel}\nAmount: ₹${amountVal}\nAccount: ${acctLine}\nA/c Holder: ${bankHolder || '-'}\nIFSC: ${bankIfsc || '-'}\nUPI: ${bankUpi || '-'}`;

      await sendMessage(null, cardMsg, { message_type: 'text', as_customer: true });
      toast.success("Withdrawal form request created!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || "Failed to create withdrawal request");
    }
  };

  const checkSavedBankDetails = async (custUserId) => {
    if (!custUserId) return false;
    try {
      const res = await axios.get(`${API}/client-bank-details/${custUserId}`);
      const data = res.data;
      const b = data?.saved_bank_detail || data?.client_bank_detail || data?.bank_details;
      if (b && (b.bank_account_number || b.client_bank_account_number || b.upi_id || b.client_upi_id || b.ifsc_code || b.client_ifsc_code)) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleDepositHeaderClick = async () => {
    if (!roomId) return;
    try {
      const pendingRes = await axios.get(`${API}/chat/room/${roomId}/pending-deposits`);
      const pendingList = pendingRes.data || [];
      if (pendingList.length > 0) {
        setShowPendingRequestsModal(true);
      } else {
        setSelectedPendingRequest(null);
        setEditingDepositMsg(null);
        setShowDepositModal(true);
      }
    } catch (e) {
      console.error("Failed to check pending deposit requests", e);
      setSelectedPendingRequest(null);
      setEditingDepositMsg(null);
      setShowDepositModal(true);
    }
  };

  const handleCoinCutHeaderClick = async () => {
    if (!roomId) return;
    try {
      const pendingRes = await axios.get(`${API}/chat/room/${roomId}/pending-requests?type=withdrawal`);
      const pendingList = pendingRes.data || [];
      if (pendingList.length > 0) {
        setShowPendingWithdrawRequestsModal(true);
      } else {
        const countRes = await axios.get(`${API}/chat/room/${roomId}/pending-count?type=withdrawal`);
        const cooldownMins = countRes.data.cooldown_minutes || 0;
        if (cooldownMins > 0) {
          toast.error("Please wait 30 min for request new Withdrawal");
          return;
        }
        setSelectedPendingRequest(null);
        setEditingCoinCutMsg(null);
        setShowCoinCutModal(true);
      }
    } catch (e) {
      console.error("Failed to check pending withdrawal requests", e);
      setSelectedPendingRequest(null);
      setEditingCoinCutMsg(null);
      setShowCoinCutModal(true);
    }
  };

  // Support Request Modals State
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);
  const [showMarkSolvedModal, setShowMarkSolvedModal] = useState(false);
  const [pendingSupportForm, setPendingSupportForm] = useState(null);
  const [isSubmittingMarkSolved, setIsSubmittingMarkSolved] = useState(false);

  const handleCreateSupportFormClick = async () => {
    if (!roomId) return;
    try {
      const countRes = await axios.get(`${API}/chat/room/${roomId}/pending-count?type=support`);
      const pendingCount = countRes.data.count;
      if (pendingCount >= 1) {
        toast.error("You already have 1 open support request. Wait for it to close first.");
        return;
      }
      setShowSupportModal(true);
    } catch (err) {
      console.error(err);
      setShowSupportModal(true);
    }
  };

  const handleSupportSubmit = async (queryText) => {
    if (!queryText || !queryText.trim()) return;
    setIsSubmittingSupport(true);
    try {
      const res = await axios.post(`${API}/chat/form-request/create`, {
        type: 'support',
        amount: 0,
        roomId: roomId,
        description: queryText.trim()
      });
      const formRequest = res.data;

      const senderLabel = `${user?.username || 'Handler'} (${user?.role || 'handler'})`;
      const clientLabel = `${customer?.username || customer?.name || 'client'} (@${customer?.username || customer?.name || 'client'})`;
      const cardMsg = `🎫 ${formRequest.form_id} — Support R\nRequested by: ${senderLabel}\nClient: ${clientLabel}\nReference: ${queryText.trim()}`;

      // Send card as customer message so it appears on the user's side (left in handler view, right in customer view)
      await sendMessage(null, cardMsg, { as_customer: true });
      toast.success("Support form request created!");
      setShowSupportModal(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to create support request");
    } finally {
      setIsSubmittingSupport(false);
    }
  };

  const handleMarkAsSolvedClick = async () => {
    if (!roomId) return;
    const isInquiry = customer?.chat_type === 'inquiry';
    const isGuestSupport = customer?.is_chat_support === true;
    try {
      const pendingRes = await axios.get(`${API}/chat/room/${roomId}/pending-requests?type=support`);
      const pendingList = pendingRes.data || [];
      if (pendingList.length > 0) {
        setPendingSupportForm(pendingList[0]);
        setShowMarkSolvedModal(true);
      } else {
        if (!isInquiry) {
          toast.error("No pending support is opened");
          return;
        }
        const solverName = user?.username || user?.name || 'Handler';
        const msgText = `✅ Mark as solved by - ${solverName}`;
        await sendMessage(null, msgText, { is_internal: false });
        if (isGuestSupport && customer?.inquiry_id) {
          await axios.post(`${API}/guest-inquiries/${customer.inquiry_id}/solve`).catch((error) => {
            console.error('Failed to close guest inquiry:', error);
          });
        }
        toast.success("Marked as solved!");
      }
    } catch (e) {
      if (!isInquiry) {
        toast.error("No pending support is opened");
        return;
      }
      const solverName = user?.username || user?.name || 'Handler';
      const msgText = `✅ Mark as solved by - ${solverName}`;
      await sendMessage(null, msgText, { is_internal: false });
      toast.success("Marked as solved!");
    }
  };

  const handleMarkSolvedSubmit = async (noteText) => {
    if (!pendingSupportForm) return;
    setIsSubmittingMarkSolved(true);
    const isInquiry = customer?.chat_type === 'inquiry';
    const isGuestSupport = Boolean(customer?.is_chat_support);
    try {
      await axios.post(`${API}/chat/form-request/${pendingSupportForm.id}/approve`, {
        description: noteText || ''
      });

      const solverName = user?.username || user?.name || 'Handler';
      // Normal support includes the remark; inquiry omits it
      const msgText = isInquiry
        ? `✅ Mark as solved by - ${solverName}`
        : noteText && noteText.trim()
          ? `✅ Mark as solved by - ${solverName}\nRemark : ${noteText.trim()}`
          : `✅ Mark as solved by - ${solverName}`;
      await sendMessage(null, msgText, { is_internal: false });
      if (isGuestSupport && customer?.inquiry_id) {
        await axios.post(`${API}/guest-inquiries/${customer.inquiry_id}/solve`).catch((error) => {
          console.error('Failed to close guest inquiry:', error);
        });
      }

      toast.success("Support request marked as solved!");
      setShowMarkSolvedModal(false);
      setPendingSupportForm(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to mark support request as solved");
    } finally {
      setIsSubmittingMarkSolved(false);
    }
  };

  const handleSaveEditWithdraw = async ({ formId, oldAmount, newAmount, newReference, newUtr, newPaidRef, newPaidBank }) => {
    try {
      const cleanForm = formId ? String(formId).trim().toUpperCase() : '';
      const reqNum = cleanForm ? (cleanForm.startsWith('W') ? cleanForm : `W${cleanForm}`) : 'W1';
      const noticeText = `🔔 आपका request ${reqNum} एक गलती की वजह से हमें update करना पड़ा। Amount ${oldAmount} से बदलकर ${newAmount} कर दिया गया है। अगर कोई concern हो तो हमारे support person से contact करें।`;
      await sendMessage(null, noticeText);

      if (editingWithdrawMsg) {
        let updatedText = editingWithdrawMsg.text || '';
        if (newAmount) {
          updatedText = updatedText.replace(/(Aapke\s+)[0-9.]+(\s+coin cut)/gi, `$1${newAmount}$2`);
          updatedText = updatedText.replace(/(Amount:\s*₹?\s*)[0-9.]+/gi, `$1${newAmount}`);
          updatedText = updatedText.replace(/(Coins ₹\s*)[0-9.]+/gi, `$1${newAmount}`);
        }
        if (newUtr) {
          updatedText = updatedText.replace(/(UTR\s*(?:Number)?:?\s*)[^\n]+/gi, `$1${newUtr}`);
        }
        if (newPaidBank) {
          updatedText = updatedText.replace(/(Paid From:\s*)[^\n]+/gi, `$1${newPaidBank}`);
        }

        await axios.put(`${API}/chat/message/${editingWithdrawMsg.id}/edit`, {
          user_id: user.id,
          text: updatedText || editingWithdrawMsg.text || noticeText,
          amount: parseFloat(newAmount),
          utr: newUtr,
          paid_from: newPaidBank,
          description: newReference,
          form_id: reqNum
        });

        setMessages(prev => prev.map(m => m.id === editingWithdrawMsg.id ? {
          ...m,
          text: updatedText || m.text,
          amount: parseFloat(newAmount),
          utr: newUtr,
          paid_from: newPaidBank,
          description: newReference,
          edited: true
        } : m));

        // Also update Coin Cut message text in room if found
        const coinCutMsg = messages.find(m => {
          const mt = m.message_type || m.media?.message_type;
          const txt = (m.text || m.message_text || m.media?.message_text || '').toLowerCase();
          return mt === 'coin_cut' || txt.includes('coin cut');
        });

        if (coinCutMsg && newAmount) {
          const origText = coinCutMsg.text || coinCutMsg.message_text || coinCutMsg.media?.message_text || '';
          let updatedCoinCutText = origText
            .replace(/(Aapke\s+)\d+(?:\.\d+)?(\s+coin cut)/gi, `$1${newAmount}$2`)
            .replace(/(Coins\s*₹?\s*)\d+(?:\.\d+)?/gi, `Coins ₹${newAmount}`);

          if (updatedCoinCutText && updatedCoinCutText !== origText) {
            try {
              await axios.put(`${API}/chat/message/${coinCutMsg.id}/edit`, {
                user_id: user.id,
                text: updatedCoinCutText,
                amount: parseFloat(newAmount)
              });
              setMessages(prev => prev.map(m => m.id === coinCutMsg.id ? {
                ...m,
                text: updatedCoinCutText,
                message_text: updatedCoinCutText,
                amount: parseFloat(newAmount),
                edited: true
              } : m));
            } catch (e) {
              console.error("Failed to edit coin cut message:", e);
            }
          }
        }
      }

      toast.success("Withdrawal updated successfully");
      setShowEditWithdrawModal(false);
      setEditingWithdrawMsg(null);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || err.response?.data?.error || "Failed to update withdraw entry");
    }
  };

  const [cancelRemarkTarget, setCancelRemarkTarget] = useState(null);

  const handleCancelRequest = (req) => {
    setCancelRemarkTarget(req);
  };

  const handleConfirmCancelWithRemark = async (req, remark) => {
    try {
      const res = await axios.post(`${API}/chat/form-request/${req.id}/cancel`, { reason: remark });
      toast.success("Request cancelled successfully");
      setCancelRemarkTarget(null);
      setShowPendingRequestsModal(false);
      setShowPendingWithdrawRequestsModal(false);
      if (res.data?.message) {
        setMessages(prev => {
          if (prev.some(m => m.id === res.data.message.id)) return prev;
          return [...prev, res.data.message];
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || "Failed to cancel request");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Handler for WhatsAppInput typing indicator
  const handleTypingIndicator = (isTyping) => {
    if (roomId && user?.id) {
      sendTypingIndicator(roomId, user.id, isTyping);
      
      if (isTyping) {
        if (typingTimeout) clearTimeout(typingTimeout);
        
        const timeout = setTimeout(() => {
          sendTypingIndicator(roomId, user.id, false);
        }, 3000);
        
        setTypingTimeout(timeout);
      }
    }
  };

  // Handler for WhatsAppInput send message
  const handleWhatsAppSendMessage = (text) => {
    sendMessage(null, text);
  };

  const handleProfileClick = () => {
    if (customer) {
      const isMobile = window.innerWidth < 768;
      if (onOpenProfile && !isMobile) {
        onOpenProfile(customer.id, roomId);
      } else {
        navigate(`/handler-profile/${customer.id}?from_room=${roomId}`);
      }
    }
  };

  // Long press handlers
  const touchStartPos = useRef(null);
  
  const handleTouchStart = (messageId, e) => {
    // Don't trigger on child interactive elements
    if (e.target.tagName === 'IMG' || e.target.tagName === 'AUDIO' || e.target.closest('audio')) {
      return;
    }

    if (!e.touches && Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
      setLongPressMenu({
        messageId,
        x: e.clientX,
        y: e.clientY
      });
      return;
    }
    
    // Check if touches array exists
    if (!e.touches || e.touches.length === 0) {
      return;
    }
    
    // Store initial touch position
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    
    longPressTimer.current = setTimeout(() => {
      setLongPressMenu({
        messageId,
        x: touchStartPos.current.x,
        y: touchStartPos.current.y
      });
    }, 500);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    touchStartPos.current = null;
  };

  const handleTouchMove = (e) => {
    // Cancel long press only if finger moves more than 10px
    if (longPressTimer.current && touchStartPos.current && e.touches && e.touches.length > 0) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.current.x);
      const deltaY = Math.abs(currentY - touchStartPos.current.y);
      
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    }
  };

  // Double tap for emoji
  const lastTap = useRef(0);
  const handleDoubleTap = (messageId) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setShowEmojiPicker(messageId);
    }
    lastTap.current = now;
  };

  // Message actions
  const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText).then(() => {
      toast.success('Copied', { duration: 1500, style: { background: 'rgba(37,211,102,0.9)', color: '#fff', fontSize: '13px', padding: '6px 16px', minHeight: 'auto' } });
    }).catch(() => {});
    setLongPressMenu(null);
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setLongPressMenu(null);
    setShowDeleteModal(true);
  };

  const [isDeletingMessage, setIsDeletingMessage] = useState(false);
  const [deletingAction, setDeletingAction] = useState(null);

  const handleDeleteMessage = async (deleteForEveryone) => {
    if (!messageToDelete) return;
    setIsDeletingMessage(true);
    setDeletingAction(deleteForEveryone ? 'everyone' : 'me');

    try {
      await axios.delete(`${API}/chat/message/${messageToDelete}`, {
        params: { user_id: user.id, delete_for_everyone: deleteForEveryone }
      });

      setMessages(prev => prev.filter(msg => msg.id !== messageToDelete));
      toast.success(deleteForEveryone ? "Message deleted for everyone" : "Message deleted");
      setShowDeleteModal(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error.response?.data?.error || 'Failed to delete message');
    } finally {
      setIsDeletingMessage(false);
      setDeletingAction(null);
    }
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/chat/message/${messageId}/reaction`, null, {
        params: { user_id: user.id, emoji }
      });

      // Update local state
      setMessages(messages.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || {};
          if (reactions[emoji] && reactions[emoji].includes(user.id)) {
            // Remove reaction
            reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
            if (reactions[emoji].length === 0) delete reactions[emoji];
          } else {
            // Add reaction
            if (!reactions[emoji]) reactions[emoji] = [];
            reactions[emoji].push(user.id);
          }
          return { ...msg, reactions };
        }
        return msg;
      }));

      setShowEmojiPicker(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const handleReplyMessage = (msg) => {
    setReplyingTo(msg);
    setLongPressMenu(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleForward = async (messageId) => {
    try {
      const msg = messages.find(m => m.id === messageId);
      if (!msg) return;
      
      setMessageToForward(msg);
      
      // Fetch all available chats (customer rooms)
      const response = await axios.get(`${API}/chat/rooms`, {
        params: { user_id: user.id, role: user.role, chat_type: user.chat_type }
      });
      
      const otherChats = response.data.filter(chat => chat.id !== roomId);
      setAvailableChats(otherChats);
      setShowForwardModal(true);
      setLongPressMenu(null);
    } catch (error) {
      console.error('Failed to load chats:', error);
      toast.error('Failed to load chats');
    }
  };

  const forwardToChat = async (targetRoomIds) => {
    try {
      if (!messageToForward) return;
      // Accept single id or array
      const ids = Array.isArray(targetRoomIds) ? targetRoomIds : [targetRoomIds];
      if (ids.length === 0) return;

      const results = await Promise.allSettled(
        ids.map((rid) =>
          axios.post(`${API}/chat/send-message`, {
            room_id: rid,
            sender_id: user.id,
            text: isStickerMessage(messageToForward) ? '' : (messageToForward.text || '📷 Forwarded media'),
            message_type: isStickerMessage(messageToForward) ? 'sticker' : undefined,
            image_url: messageToForward.image_url,
            voice_url: messageToForward.voice_url,
          })
        )
      );

      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;

      setShowForwardModal(false);
      setMessageToForward(null);

      if (okCount > 0 && failCount === 0) {
        toast.success(okCount === 1 ? 'Message forwarded!' : `Forwarded to ${okCount} chats`);
      } else if (okCount > 0 && failCount > 0) {
        toast.warning(`Forwarded to ${okCount}, failed for ${failCount}`);
      } else {
        toast.error('Failed to forward');
      }
    } catch (error) {
      console.error('Forward failed:', error);
      toast.error('Failed to forward');
    }
  };

  // Forward to Group
  const handleForwardToGroup = async (msg) => {
    try {
      setForwardGroupMsg(msg);
      const response = await axios.get(`${API}/groups/list`, { params: { user_id: user.id } });
      setAvailableGroups(
        Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.groups)
          ? response.data.groups
          : []
      );
      setShowForwardGroupModal(true);
      setLongPressMenu(null);
    } catch (error) {
      console.error('Failed to load groups:', error);
      toast.error('Failed to load groups');
    }
  };

  const forwardToGroup = async (groupId) => {
    if (!forwardGroupMsg) return;
    try {
      const msgData = {
        group_id: groupId,
        sender_id: user.id,
        sender_name: user.username || user.name,
        sender_role: user.role,
        text: forwardGroupMsg.text || '',
        image_url: forwardGroupMsg.image_url || null,
        voice_url: forwardGroupMsg.voice_url || null,
        is_forwarded: true
      };
      await axios.post(`${API}/groups/${groupId}/send-message`, msgData);
      toast.success('Forwarded to group!');
      setShowForwardGroupModal(false);
      setForwardGroupMsg(null);
    } catch (error) {
      console.error('Forward to group failed:', error);
      toast.error('Failed to forward');
    }
  };

  const handleMoveChat = async (newType) => {
    try {
      const res = await axios.post(`${API}/chat/rooms/${roomId}/move`, {
        new_chat_type: newType,
        user_id: user.id
      });
      toast.success(`Customer moved to ${newType}!`);
      setShowMoveMenu(false);
      setShowMenu(false);
      // Handler stays on OWN dashboard (don't follow customer to new panel)
      if (onBackToList) {
        onBackToList();
      } else {
        navigate('/handler-dashboard');
      }
    } catch (e) {
      toast.error('Failed to move customer');
    }
  };

  const handleAssignMB = async (mb) => {
    if (!customer?.id) return;
    const isGuest = Boolean(customer?.is_guest || customer?.phone?.startsWith('GUEST_'));
    if (isGuest) {
      toast.error("Please register this customer before assigning an MB setup.");
      alert("Please register this customer before assigning an MB setup.");
      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
      setShowRegisterModal(true);
      setShowMBMenu(false);
      setShowMenu(false);
      return;
    }
    try {
      await axios.patch(`${API}/clients/${customer.id}/mb-setup`, {
        mb_setup: mb,
        actor_id: user.id
      });
      setCustomer((prev) => (prev ? { ...prev, mb_setup: mb } : prev));
      toast.success(mb ? `Assigned to ${mb}` : 'MB tag removed');
      setShowMBMenu(false);
      setShowMenu(false);
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.detail || 'Failed to assign MB setup');
    }
  };

  const handleBlockCustomer = async () => {
    if (!customer?.id) return;
    try {
      const currentlyBlocked = customer.blocked || customer.is_blocked;
      if (currentlyBlocked) {
        await axios.post(`${API}/clients/unblock/${customer.id}`);
        setCustomer(prev => (prev ? { ...prev, blocked: false, is_blocked: false } : prev));
        toast.success('Customer unblocked!');
      } else {
        await axios.post(`${API}/clients/block`, { customer_id: customer.id });
        setCustomer(prev => (prev ? { ...prev, blocked: true, is_blocked: true } : prev));
        toast.success('Customer blocked!');
      }
      setShowMenu(false);
    } catch (e) {
      toast.error('Failed to toggle block status');
    }
  };

  const handleMediaSelected = async (media) => {
    try {
      let messageText = '';
      let imageUrl = null;
      let voiceUrl = null;
      let documentUrl = null;
      let documentName = null;
      let localPreviewUrl = null;
      
      if (media.type === 'image') {
        messageText = media.caption || '';
        imageUrl = media.url;
        // Create local preview for optimistic UI
        if (media.file) {
          localPreviewUrl = URL.createObjectURL(media.file);
        }
      } else if (media.type === 'video') {
        messageText = media.caption || '🎥 Video';
        imageUrl = media.url;
      } else if (media.type === 'voice') {
        messageText = '🎤 Voice';
        voiceUrl = media.url;
      } else if (media.type === 'sticker') {
        messageText = '';
        imageUrl = media.url;
      } else if (media.type === 'document') {
        messageText = '📄 ' + (media.fileName || 'Document');
        documentUrl = media.url;
        documentName = media.fileName || 'Document';
      } else if (media.type === 'text') {
        messageText = media.text || '';
      }
      
      // Optimistic UI: Show message immediately with local preview
      const optimisticId = 'optimistic-' + Date.now();
      if (localPreviewUrl && media.type === 'image') {
        const optimisticMsg = {
          id: optimisticId,
          sender_id: user.id,
          sender_name: user.username,
          text: messageText,
          image_url: localPreviewUrl,
          created_at: new Date().toISOString(),
          _uploading: true
        };
        setMessages(prev => [...prev, optimisticMsg]);
        setIsUserScrolling(false);
      }
      
      const response = await axios.post(`${API}/chat/send-message`, {
        room_id: roomId,
        sender_id: user.id,
        text: messageText,
        image_url: imageUrl,
        voice_url: voiceUrl,
        document_url: documentUrl,
        message_type: media.type === 'sticker' ? 'sticker' : undefined,
        document_name: documentName
      });

      // Replace optimistic message with real one (dedup safe)
      notifiedMessageIds.current.add(response.data.id);
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
        setMessages(prev => prev.map(m => m.id === optimisticId ? response.data : m));
      } else {
        setMessages(prev => {
          if (prev.some(m => m.id === response.data.id)) return prev;
          return [...prev, response.data];
        });
      }
      setIsUserScrolling(false);
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Failed to send media');
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => !m._uploading));
    }
  };

  const handleConfirmCancelDeposit = async () => {
    if (!cancellingMsg) return;
    try {
      const res = await axios.post(`${API}/chat/message/${cancellingMsg.id}/cancel-deposit`, {
        reason: cancelReason
      });
      if (res.data.success) {
        setMessages(prev => prev.map(m => m.id === cancellingMsg.id ? res.data.message : m));
        toast.success('Deposit cancelled successfully');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancellingMsg(null);
      setCancelReason('');
    }
  };

  // Loading state removed - UI renders instantly

  // Preserve the department/sub-tab that opened this chat on mobile/tablet.
  // If the route was opened directly, the room metadata supplies the fallback.
  const getHandlerDashboardBackState = () => {
    const roomChatType = customer?.chat_type || '';
    let chatTypeTab = location.state?.chatTypeTab || roomChatType || user?.chat_type || 'deposit';
    const supportSubTab = location.state?.supportSubTab || (roomChatType === 'inquiry' ? 'inquiry' : 'support');

    // Deposit/calling handlers show inquiry rooms inside the Support tab.
    if (roomChatType === 'inquiry' && chatTypeTab !== 'inquiry' && (user?.role === 'deposit_handler' || user?.role === 'calling_handler')) {
      chatTypeTab = 'support';
    }

    return {
      chatTypeTab,
      ...(chatTypeTab === 'support' ? { supportSubTab } : {}),
      ...(location.state?.mbFilter ? { mbFilter: location.state.mbFilter } : {})
    };
  };

  return (
    <div ref={containerRef} className={`${embeddedRoomId ? 'relative w-full h-full' : 'fixed inset-0'} bg-wa-hover overflow-hidden`}>
      {/* Header - WhatsApp Style */}
      <div className="absolute top-0 left-0 right-0 bg-wa-panel-header border-b border-[#e9edef] z-50">
        <div className="flex items-center h-14 px-2">
          {/* Back Button */}
          <button
            onClick={() => {
              if (onBackToList) { onBackToList(); return; }
              navigate('/handler-dashboard', { state: getHandlerDashboardBackState() });
            }}
            className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
          >
            <ArrowLeft size={24} />
          </button>

          {/* Profile Section - Clickable */}
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-3 flex-1 hover:bg-wa-hover py-1 px-2 rounded-lg transition-colors"
          >
            {/* Profile Picture */}
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {customer?.profile_pic ? (
                <img 
                  src={customer.profile_pic.startsWith('http') ? customer.profile_pic : `${API}${customer.profile_pic}`} 
                  alt={customer.name} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center text-wa-text-secondary font-semibold text-lg ${customer?.profile_pic ? 'hidden' : ''}`}>
                {customer?.name ? customer.name.charAt(0).toUpperCase() : 'C'}
              </div>
            </div>
            
            {/* Name and Status */}
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-1.5">
                <h2 className="text-base font-semibold text-wa-text-primary truncate">{customer?.name || 'Customer'}</h2>
                <MBSetupBadge value={customer?.mb_setup} />
              </div>
              <div className="flex items-center gap-1.5 truncate min-w-0">
                <OnlineStatus isOnline={customerOnlineStatus.is_online} lastSeen={customerOnlineStatus.last_seen} showText={true} textColor="text-wa-text-secondary" textSize="text-xs" />
                {customer?.address && (
                  <span className="hidden sm:inline text-xs font-medium text-slate-600 dark:text-slate-300 truncate">
                    - {customer.address}
                  </span>
                )}
              </div>
            </div>
          </button>

          {/* Right Header Section - Pack all buttons in a row */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            

            {/* WhatsApp Redirect Button */}
            <button
              onClick={async () => {
                if (!customer?.id && !roomId) { toast.error('Customer info not available'); return; }
                try {
                  await axios.post(`${API}/calling-reports/activity`, {
                    room_id: roomId,
                    customer_id: customer?.id,
                    kind: 'whatsapp'
                  });
                  toast.success('WhatsApp redirect logged in chat');
                } catch (e) { console.error(e); }
                if (customer?.phone) {
                  const cleanNumber = customer.phone.replace(/\D/g, '');
                  const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
                  window.open(`https://wa.me/${whatsappNumber}`, '_blank');
                }
              }}
              className="p-2 hover:bg-wa-hover rounded-full text-emerald-600 cursor-pointer"
              title="WhatsApp Redirect"
              data-testid="chat-whatsapp-btn"
            >
              <WhatsappLogo size={22} weight="fill" />
            </button>

            {/* Audio Call Button - Log Call Activity */}
            <button
              onClick={async () => {
                if (!customer?.id && !roomId) { toast.error('Customer info not available'); return; }
                try {
                  await axios.post(`${API}/calling-reports/activity`, {
                    room_id: roomId,
                    customer_id: customer?.id,
                    kind: 'call'
                  });
                  toast.success('Call activity logged in chat');
                } catch (e) { console.error(e); }
                if (user?.role !== 'calling_handler' && customer?.phone) {
                  window.location.href = `tel:${customer.phone}`;
                }
              }}
              className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary cursor-pointer"
              title="Call Phone"
              data-testid="audio-call-btn"
            >
              <Phone size={22} />
            </button>

            {/* Menu Button */}
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
              >
                <DotsThreeVertical size={24} />
              </button>
            
            {/* Dropdown Menu */}
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#111b21] opacity-100 rounded-lg shadow-lg py-2 z-50">
                <button
                  onClick={() => {
                    handleProfileClick();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-wa-text-primary hover:bg-wa-hover flex items-center gap-2"
                >
                  <UserCircle size={20} />
                  View Profile
                </button>
                {customer?.is_guest && (
                  <button
                    onClick={() => {
                      setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
                      setShowRegisterModal(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-blue-600 hover:bg-wa-hover flex items-center gap-2"
                  >
                    <Plus size={20} />
                    Register Customer
                  </button>
                )}
                {!customer?.is_guest && customer?.role === 'customer' && user?.role !== 'calling_handler' && (
                  <button
                    onClick={async () => {
                      setShowMenu(false);
                      try {
                        const res = await axios.get(`${API}/clients/registered/${customer.id}`);
                        if (res.data) {
                          setEditClientData(res.data);
                          setShowEditModal(true);
                        } else {
                          toast.error('Client data not found');
                        }
                      } catch (e) {
                        toast.error('Failed to load client data');
                      }
                    }}
                    className="w-full px-4 py-2 text-left text-wa-text-primary hover:bg-wa-hover flex items-center gap-2"
                  >
                    <FileText size={20} />
                    Edit
                  </button>
                )}
                {permissions?.can_block_clients && (
                  <button
                    onClick={handleBlockCustomer}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-wa-hover flex items-center gap-2"
                  >
                    <Prohibit size={20} />
                    {customer?.blocked || customer?.is_blocked ? 'Unblock Customer' : 'Block Customer'}
                  </button>
                )}
                {/* Move Chat - Restricted to Super Admin only */}
                {user?.role === 'super_admin' && (
                  <div className="border-t mt-1 pt-1">
                    <button
                      onClick={() => setShowMoveMenu(!showMoveMenu)}
                      className="w-full px-4 py-2 text-left text-orange-600 hover:bg-wa-hover flex items-center gap-2"
                    >
                      <ArrowsLeftRight size={20} />
                      Move to...
                    </button>
                    {showMoveMenu && (
                      <div className="bg-wa-hover py-1">
                        {['deposit', 'withdrawal', 'support', 'inquiry']
                          .filter(type => type !== (user.chat_type || ''))
                          .map(type => (
                          <button key={type} onClick={() => handleMoveChat(type)}
                            className="w-full px-6 py-1.5 text-left text-sm text-wa-text-primary hover:bg-wa-hover capitalize">
                            {type}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Assign to MB Setup */}
                {user?.role !== 'calling_handler' && (user?.role === 'super_admin' || (customer?.chat_type === 'inquiry' ? (user?.settings_powers?.enquiry_handler_mb !== false && user?.settings_powers?.enquiry_handler_mb !== 'false') : true)) && (customer?.role === 'customer' || customer?.is_guest) && (
                  <div className="border-t mt-1 pt-1">
                    <button
                      onClick={() => {
                        const isGuest = Boolean(customer?.is_guest || customer?.phone?.startsWith('GUEST_'));
                        if (isGuest) {
                          toast.error("Please register this customer before assigning an MB setup.");
                          alert("Please register this customer before assigning an MB setup.");
                          setRegisterForm({ name: '', phone: '', password: 'Abcd1234' });
                          setShowRegisterModal(true);
                          setShowMBMenu(false);
                          setShowMenu(false);
                          return;
                        }
                        setShowMBMenu(!showMBMenu);
                      }}
                      className="w-full px-4 py-2 text-left text-teal-700 hover:bg-wa-hover flex items-center justify-between"
                      data-testid="assign-mb-menu-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Star size={20} />
                        Assign to MB Setup
                      </span>
                      {customer?.mb_setup && <MBSetupBadge value={customer.mb_setup} />}
                    </button>
                    {showMBMenu && (
                      <div className="bg-wa-hover py-1">
                        {((user?.role === 'super_admin' || user?.role === 'inquiry_handler' || user?.chat_type === 'inquiry' || !Array.isArray(user?.assigned_mbs) || user.assigned_mbs.length === 0 || user.assigned_mbs.map(x => String(x).toLowerCase()).includes('all'))
                          ? MB_SETUPS
                          : MB_SETUPS.filter(mb => user.assigned_mbs.some(m => String(m).toUpperCase() === String(mb).toUpperCase()))
                        ).map((mb) => (
                          <button
                            key={mb}
                            onClick={() => handleAssignMB(mb)}
                            className={`w-full px-6 py-1.5 text-left text-sm hover:bg-teal-50 flex items-center justify-between ${customer?.mb_setup === mb ? 'font-semibold text-teal-700' : 'text-wa-text-primary'}`}
                            data-testid={`assign-mb-${mb}`}
                          >
                            <span>{mb}</span>
                            {customer?.mb_setup === mb && <Check size={14} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        </div>
        
        {/* Banner Bar (Deposit or Coin Cut) */}
        {customer && user?.role !== 'calling_handler' && (
          <>
            {customer.chat_type === 'deposit' && (
              <div className="flex w-full gap-2 select-none">
                <button
                  onClick={() => {
                    setShowCreateDepositFormModal(true);
                  }}
                  className="flex-1 h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded"
                  style={{ backgroundColor: '#a855f7' }}
                >
                  <span style={{ fontSize: '15px' }}>+</span>
                  <span style={{ letterSpacing: '0.04em' }}>Create Deposit Form</span>
                </button>
                <button
                  onClick={handleDepositHeaderClick}
                  className="flex-1 h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded"
                  style={{ background: 'linear-gradient(90deg, #1a56db 0%, #3b82f6 50%, #1a56db 100%)' }}
                >
                  <span style={{ fontSize: '15px' }}>🔥</span>
                  <span style={{ letterSpacing: '0.08em' }}>Deposit</span>
                </button>
              </div>
            )}
            {customer.chat_type === 'withdrawal' && (
              <div className="flex w-full gap-2 select-none">
                <button
                  onClick={handleCreateWithdrawFormClick}
                  className="flex-1 h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded cursor-pointer"
                  style={{ backgroundColor: '#8b5cf6' }}
                >
                  <span style={{ fontSize: '15px' }}>+</span>
                  <span style={{ letterSpacing: '0.04em' }}>Create Withdraw Form</span>
                </button>
                <button
                  onClick={handleCoinCutHeaderClick}
                  className="flex-1 h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded cursor-pointer"
                  style={{ backgroundColor: '#d97706' }}
                >
                  <span style={{ fontSize: '15px' }}>✂️</span>
                  <span style={{ letterSpacing: '0.08em' }}>Coin Cut</span>
                </button>
              </div>
            )}
            {customer.chat_type === 'support' && (
              <div className="flex w-full gap-2 select-none">
                <button
                  onClick={handleCreateSupportFormClick}
                  className="flex-1 h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded cursor-pointer"
                  style={{ backgroundColor: '#8b5cf6' }}
                >
                  <span style={{ fontSize: '15px' }}>+</span>
                  <span style={{ letterSpacing: '0.04em' }}>Create Support R</span>
                </button>
                <button
                  onClick={handleMarkAsSolvedClick}
                  className="flex-1 h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded cursor-pointer"
                  style={{ backgroundColor: '#15803d' }}
                >
                  <span style={{ fontSize: '15px' }}>✓</span>
                  <span style={{ letterSpacing: '0.08em' }}>Mark as Solved</span>
                </button>
              </div>
            )}
            {customer.chat_type === 'inquiry' && (
              <div className="flex w-full select-none">
                <button
                  onClick={handleMarkAsSolvedClick}
                  className="w-full h-9 flex items-center justify-center gap-2 text-white font-semibold text-sm tracking-wide transition-opacity hover:opacity-90 active:opacity-75 rounded cursor-pointer"
                  style={{ backgroundColor: '#15803d' }}
                >
                  <span style={{ fontSize: '15px' }}>✓</span>
                  <span style={{ letterSpacing: '0.08em' }}>Mark as Solved</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Messages - Fixed position area */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="absolute left-0 right-0 overflow-y-auto p-3 wa-chat-wallpaper"
        style={{ 
          top: (customer && ['deposit', 'withdrawal', 'support', 'inquiry'].includes(customer.chat_type)) ? '92px' : '56px',
          bottom: '56px',
        }}
      >
        <div className="space-y-[3px] pb-4">
          {/* Older messages loading spinner */}
          {isLoadingOlder && (
            <div className="flex justify-center items-center py-3">
              <div className="w-5 h-5 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
              <span className="ml-2 text-xs text-gray-400">Loading older messages…</span>
            </div>
          )}
          {messages.map((msg, idx) => {
            const isOwn = msg.sender_id === user.id || (msg.sender_role && !['customer', 'inquiry_guest'].includes(msg.sender_role));
            const reactions = msg.reactions || {};
            
            // Skip if this message is part of an image group but NOT the first
            if (isPartOfImageGroup(messages, idx)) return null;
            
            if (msg.deleted_by_admin || msg.deletedByAdmin) return null;

            // Skip message if it has no text, media, call info, or internal status
            const hasContent = msg.is_internal ||
              (msg.text && msg.text.trim()) ||
              msg.image_url ||
              msg.voice_url ||
              msg.document_url ||
              msg.call_info;
            if (!hasContent) return null;

            // Check if this starts an image group
            const imageGroup = getImageGroup(messages, idx);
            
            // Check if this is the first unread INCOMING message (not own message)
            const isIncoming = !isOwn;
            const isPreviousIncoming = idx > 0 ? (() => { const prev = messages[idx - 1]; return prev?.sender_role ? ['customer', 'inquiry_guest'].includes(prev.sender_role) : prev?.sender_id !== user.id; })() : false;
            const isFirstUnread = isIncoming && !msg.read && (idx === 0 || messages[idx - 1]?.read !== false || !isPreviousIncoming);
            const unreadCount = messages.filter(m => {
              const mIsOwn = m.sender_id === user.id || (m.sender_role && !['customer', 'inquiry_guest'].includes(m.sender_role));
              return !mIsOwn && !m.read;
            }).length;
            
            const isDepositCard = msg.text && msg.text.match(/D\d+\s*[\u2014-]\s*Deposit\s*Form/i);
            const isInternalMsg = msg.is_internal || (msg.text && (msg.text.includes('Call — initiated by') || msg.text.includes('WhatsApp redirect — by') || msg.text.includes('Assigned to MB') || msg.text.includes('MB tag removed')));
            
            return (
              <React.Fragment key={idx}>
                {/* Date Separator - WhatsApp Style */}
                {shouldShowDateSeparator(messages, idx) && (
                  <div className="flex items-center justify-center my-3">
                    <div className="bg-white/90 text-wa-icon text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm" data-testid="date-separator">
                      {getDateLabel(msg.timestamp)}
                    </div>
                  </div>
                )}

                {/* Unread Messages Divider - Only for incoming messages */}
                {isFirstUnread && unreadCount > 0 && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-wa-hover text-wa-text-primary text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {unreadCount} Unread Message{unreadCount > 1 ? 's' : ''}
                    </div>
                  </div>
                )}
                
              <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                {isInternalMsg ? (
                  <div className="mb-2 flex justify-end w-full">
                    <div className="bg-[#e6f4ea] border border-[#a3cfbb] text-[#0f5132] px-3.5 py-1.5 rounded-lg text-xs font-semibold shadow-xs max-w-sm flex items-center justify-between gap-3 select-none">
                      <span className="leading-snug">{msg.text}</span>
                      <div className="flex items-center gap-1 text-[10px] text-[#0f5132]/80 font-normal flex-shrink-0 self-end">
                        <span>{formatTime(msg.timestamp || msg.created_at)}</span>
                        <Checks size={14} weight="bold" className="text-[#0f5132]" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div 
                    // Tail on the wrapper: the bubble is overflow-hidden and would clip it.
                    className={`relative max-w-[85%] group ${isStickerMessage(msg) ? '' : isOwn ? 'wa-tail-out' : 'wa-tail-in'}`}
                    onMouseEnter={() => setHoveredMsg(msg.id)}
                    onMouseLeave={() => setHoveredMsg(null)}
                  >
                      {/* Dropdown arrow */}
                      {msg.message_type !== 'coin_cut' && msg.message_type !== 'deposit' && !isDepositCard && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setLongPressMenu({
                              messageId: msg.id,
                              x: e.clientX,
                              y: e.clientY
                            });
                          }}
                          className={`absolute top-0.5 right-0.5 z-30 w-6 h-6 flex items-center justify-center rounded-full ${isOwn ? 'bg-wa-bubble-out/95 hover:bg-[#c8f7b5]' : 'bg-white/95 hover:bg-wa-hover'} shadow-sm cursor-pointer transition-opacity opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto`}
                          style={{ opacity: longPressMenu?.messageId === msg.id ? 1 : undefined }}
                          data-testid="msg-dropdown-arrow"
                        >
                          <CaretDown size={13} className="text-wa-text-secondary" weight="bold" />
                        </button>
                      )}
                      <div 
                        className={isStickerMessage(msg) ? 'relative w-fit bg-transparent' : `relative pl-2 pr-7 pt-1.5 pb-[2px] rounded-lg shadow-sm overflow-hidden ${isOwn ? 'bg-wa-bubble-out' : 'bg-wa-panel'}`}
                        onContextMenu={(e) => { e.preventDefault(); if (!isDepositCard) handleTouchStart(msg.id, e); }}
                        onTouchStart={(e) => { if (!isDepositCard) handleTouchStart(msg.id, e); }}
                        onTouchEnd={() => { if (!isDepositCard) handleTouchEnd(); }}
                        onTouchMove={() => { if (!isDepositCard) handleTouchMove(); }}
                        onMouseDown={(e) => { if (!isDepositCard) handleTouchStart(msg.id, { target: e.target, touches: [{ clientX: e.clientX, clientY: e.clientY }] }); }}
                        onMouseUp={() => { if (!isDepositCard) handleTouchEnd(); }}
                        onMouseLeave={() => { if (!isDepositCard) handleTouchEnd(); }}
                        onClick={() => { if (!isDepositCard) handleDoubleTap(msg.id); }}
                      >

                    {/* Reply Reference - WhatsApp Style */}
                    {msg.reply_to && (
                      <div className={`mb-1 pl-2 py-0.5 border-l-2 rounded text-xs ${
                        isOwn ? 'border-green-700 bg-wa-hover/30' : 'border-blue-600 bg-wa-hover/30'
                      }`}>
                        <div className="font-semibold text-[10px] opacity-70">
                          {msg.reply_to.sender_name}
                        </div>
                        <div className="truncate text-[11px] opacity-60">
                          {msg.reply_to.text || '📷 Media'}
                        </div>
                      </div>
                    )}
                    
                    {/* Image Group Grid - WhatsApp Style */}
                    {imageGroup && imageGroup.length > 1 && (
                      <div className={`grid ${imageGroup.length === 2 ? 'grid-cols-2' : 'grid-cols-2'} gap-0.5 rounded-lg overflow-hidden mb-1.5`}>
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => {
                          const gUrl = buildMediaUrl(gMsg.image_url);
                          return (
                            <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(gUrl)}>
                              <ChatImage src={gUrl} alt="" className="w-full h-[120px] object-cover" />
                              {gIdx === 3 && imageGroup.length > 4 && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Group caption below grid */}
                    {imageGroup && imageGroup.length > 1 && (() => {
                      const lastMsg = imageGroup[imageGroup.length - 1];
                      const captionText = lastMsg.caption || (lastMsg.text && lastMsg.text.trim() && lastMsg.text !== '🖼️ Photo' && lastMsg.text !== '🎥 Video' ? lastMsg.text : '');
                      return captionText ? <p className="text-sm mb-1">{captionText}</p> : null;
                    })()}

                    {/* Single Image or Video */}
                    {msg.image_url && isStickerMessage(msg) && (
                      <StickerImage src={buildMediaUrl(msg.image_url)} />
                    )}
                    {msg.image_url && !imageGroup && !isStickerMessage(msg) && (
                      isVideoUrl(msg.image_url) ? (
                        <video
                          src={buildMediaUrl(msg.image_url)}
                          controls
                          className="rounded-lg max-w-[250px] max-h-[300px] mb-1.5"
                          preload="metadata"
                          data-testid="video-message"
                        />
                      ) : (
                        <ChatImage
                          src={buildMediaUrl(msg.image_url)}
                          alt="Shared" 
                          className="rounded-lg max-w-[250px] max-h-[300px] object-cover mb-1.5 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => handleImageClick(buildMediaUrl(msg.image_url))}
                          onError={(e) => {
                            console.error('Image load error:', msg.image_url);
                          }}
                        />
                      )
                    )}
                    
                    {/* Voice Message */}
                    {msg.voice_url && (
                      <VoiceMessage 
                        audioUrl={(() => {
                          if (msg.voice_url.startsWith('http')) {
                            return msg.voice_url;
                          } else if (msg.voice_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else if (msg.voice_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else {
                            return `${API}${msg.voice_url}`;
                          }
                        })()}
                        isSender={isOwn}
                      />
                    )}
                    
                    {/* Document Display - Clickable */}
                    {msg.document_url && (
                      <a
                        href={(() => {
                          if (msg.document_url.startsWith('http')) {
                            return msg.document_url;
                          } else if (msg.document_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else if (msg.document_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else {
                            return `${API}${msg.document_url}`;
                          }
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-wa-hover rounded-lg hover:bg-wa-hover transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText size={24} className="text-white" weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-wa-text-primary truncate">
                            {msg.document_name || 'Document'}
                          </p>
                          <p className="text-xs text-wa-text-secondary">Tap to open</p>
                        </div>
                      </a>
                    )}
                    
                    {/* Call Info Bubble - WhatsApp Style */}
                    {msg.call_info ? (
                      <div className="flex items-center gap-2 py-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${msg.call_info.status === 'completed' ? 'bg-wa-hover' : 'bg-wa-hover'}`}>
                          <Phone size={16} weight="fill" className={`${msg.call_info.status === 'completed' ? 'text-green-600' : 'text-red-500'} ${msg.call_info.caller_id === user?.id ? 'rotate-[135deg]' : ''}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-wa-text-primary">Voice call</p>
                          <p className={`text-xs ${msg.call_info.status === 'completed' ? 'text-wa-text-secondary' : 'text-red-500'}`}>
                            {msg.call_info.status === 'completed' ? (msg.call_info.duration >= 60 ? `${Math.floor(msg.call_info.duration/60)} min` : `${msg.call_info.duration} secs`) : 'No answer'}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Text + WhatsApp-style inline time */}
                    {msg.text && !msg.call_info ? (
                      <div className="leading-[19px]">
                        <span className="text-[14.5px] text-wa-text-primary break-words whitespace-pre-line">
                          {renderMessageText(msg.text)}
                        </span>
                        <span className="float-right ml-[8px] mt-[3px] flex items-center gap-[3px] leading-none">
                          {msg.starred_by?.includes(user.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                          {msg.edited && <span className="text-[10.5px] text-wa-text-secondary italic">edited</span>}
                          <span className="text-[11px] text-wa-text-secondary">
                            {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) 
                              ? new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                              : ''}
                          </span>
                          {isOwn && (() => {
                            const delivered = msg.delivered !== undefined ? msg.delivered : true;
                            const read = msg.read !== undefined ? msg.read : false;
                            if (read) return <Checks size={16} weight="bold" className="text-[#53bdeb]" />;
                            if (delivered) return <Checks size={16} weight="bold" className="text-wa-text-secondary" />;
                            return <Check size={16} weight="bold" className="text-wa-text-secondary" />;
                          })()}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-end items-center gap-[3px] mt-[2px] mb-[1px]">
                        {msg.starred_by?.includes(user.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                        {msg.edited && <span className="text-[10.5px] text-wa-text-secondary italic">edited</span>}
                        <span className="text-[11px] text-wa-text-secondary leading-none">
                          {msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) 
                            ? new Date(msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                            : ''}
                        </span>
                        {isOwn && (() => {
                          const delivered = msg.delivered !== undefined ? msg.delivered : true;
                          const read = msg.read !== undefined ? msg.read : false;
                          if (read) return <Checks size={16} weight="bold" className="text-[#53bdeb]" />;
                          if (delivered) return <Checks size={16} weight="bold" className="text-wa-text-secondary" />;
                          return <Check size={16} weight="bold" className="text-wa-text-secondary" />;
                        })()}
                      </div>
                    )}
                    


                    {/* Edit button for Deposit messages */}
                    {msg.message_type === 'deposit' && isOwn && (
                      <div className="mt-1 flex items-center gap-3">
                        {!msg.cancelled ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingDepositMsg(msg);
                            }}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs font-bold transition-colors cursor-pointer"
                          >
                            ➡ Edit
                          </button>
                        ) : (
                          <span className="text-red-500 text-xs font-bold italic">✕ Deposit cancel</span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Reactions */}
                  {Object.keys(reactions).length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {Object.entries(reactions).map(([emoji, userIds]) => (
                        <div 
                          key={emoji}
                          className="bg-wa-panel rounded-full px-2 py-1 text-xs shadow-sm border flex items-center gap-1"
                        >
                          <span>{emoji}</span>
                          <span className="text-wa-text-secondary">{userIds.length}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker === msg.id && (
                    <div className="absolute bottom-full mb-2 bg-wa-panel rounded-lg shadow-lg p-2 flex gap-2 z-50">
                      {EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className="text-2xl hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} style={{ overflowAnchor: 'auto', height: '1px' }} />
          
          {/* Typing Indicator */}
          {customerTyping && (
            <div className="flex justify-start">
              <div className="bg-wa-panel px-4 py-2 rounded-lg shadow">
                <TypingIndicator username={customer?.name || "Customer"} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to Bottom Arrow - WhatsApp Style */}
      {isUserScrolling && (
        <button
          onClick={() => { setIsUserScrolling(false); scrollToBottom(); }}
          className={`${embeddedRoomId ? 'absolute' : 'fixed'} bottom-24 right-4 z-40 w-10 h-10 bg-wa-panel rounded-full shadow-lg flex items-center justify-center border border-wa-border`}
          data-testid="scroll-to-bottom-btn"
        >
          <CaretDown size={20} className="text-wa-icon" weight="bold" />
        </button>
      )}

      {/* Input - WhatsApp Style */}
      {(() => {
        const isCallingHandler = user?.role === 'calling_handler';
        const activeChatType = customer?.chat_type || location.state?.chatTypeTab || '';
        const isSupportChat = activeChatType === 'support' || (location.pathname || '').includes('support');
        const canSendMessages = !isCallingHandler || isSupportChat;

        if (!canSendMessages) {
          return (
            <div className="absolute bottom-0 left-0 right-0 z-50 p-3.5 bg-slate-100 border-t border-slate-200 text-center text-slate-600 font-semibold text-xs shadow-inner flex items-center justify-center gap-2">
              <span>🔒</span>
              <span>Read-Only Access (Calling Handler)</span>
            </div>
          );
        }

        return (
          <div className="absolute bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]">
            <WhatsAppInput
              onSendMessage={handleWhatsAppSendMessage}
              onSendMedia={handleMediaSelected}
              onOpenMediaPicker={() => setShowMediaPicker(true)}
              onTyping={handleTypingIndicator}
              placeholder="Type a message..."
              disabled={false}
              replyingTo={replyingTo}
              onCancelReply={cancelReply}
              userId={user?.id}
              userRole="handler"
            />
          </div>
        );
      })()}

      {/* Long Press Menu */}
      {longPressMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setLongPressMenu(null)} />
          <div 
            className="fixed bg-wa-panel rounded-lg shadow-xl py-2 z-50 w-[180px]"
            style={{ 
              top: Math.max(10, Math.min(longPressMenu.y, window.innerHeight - 320)),
              left: Math.max(10, Math.min(longPressMenu.x - 80, window.innerWidth - 190))
            }}
          >
            <button
              onClick={() => {
                const msg = messages.find(m => m.id === longPressMenu.messageId);
                if (msg) handleCopyMessage(msg.text);
              }}
              className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2"
            >
              <Copy size={20} />
              Copy
            </button>
            
            {/* Reply Button */}
            <button
              onClick={() => {
                const msg = messages.find(m => m.id === longPressMenu.messageId);
                if (msg) handleReplyMessage(msg);
              }}
              className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2"
            >
              <ArrowBendUpLeft size={20} />
              Reply
            </button>

            {/* Star Button */}
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/chat/message/${longPressMenu.messageId}/star?user_id=${user.id}`);
                  setMessages(prev => prev.map(m => {
                    if (m.id === longPressMenu.messageId) {
                      const starred = m.starred_by || [];
                      return res.data.starred 
                        ? { ...m, starred_by: [...starred, user.id] }
                        : { ...m, starred_by: starred.filter(id => id !== user.id) };
                    }
                    return m;
                  }));
                  toast(res.data.starred ? 'Message starred' : 'Star removed', { duration: 1500 });
                } catch (err) { toast.error('Failed to star message'); }
                setLongPressMenu(null);
              }}
              className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2"
              data-testid="star-message-btn"
            >
              <Star size={20} weight={(() => { const msg = messages.find(m => m.id === longPressMenu.messageId); return msg?.starred_by?.includes(user.id) ? 'fill' : 'regular'; })()} className={(() => { const msg = messages.find(m => m.id === longPressMenu.messageId); return msg?.starred_by?.includes(user.id) ? 'text-yellow-500' : ''; })()} />
              {(() => { const msg = messages.find(m => m.id === longPressMenu.messageId); return msg?.starred_by?.includes(user.id) ? 'Unstar' : 'Star'; })()}
            </button>
            
            {/* Hide delete for withdrawal, coin_cut and deposit messages */}
            {(() => {
              const msg = messages.find(m => m.id === longPressMenu.messageId);
              if (!msg) return false;
              const isWithdraw = msg.message_type === 'coin_cut' || msg.message_type === 'deposit' || 
                (msg.text && (msg.text.includes('Aapka withdraw') || msg.text.includes('coin cut') || msg.text.includes('W110') || msg.text.includes('W00')));
              return !isWithdraw;
            })() && (
              <button
                onClick={() => handleDeleteClick(longPressMenu.messageId)}
                className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2 text-red-600"
              >
                <Trash size={20} />
                Delete
              </button>
            )}
            <button
              onClick={() => handleForward(longPressMenu.messageId)}
              className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2"
            >
              <ShareFat size={20} />
              Forward
            </button>
            <button
              onClick={() => {
                const msg = messages.find(m => m.id === longPressMenu.messageId);
                if (msg) handleForwardToGroup(msg);
              }}
              className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2 text-blue-600"
            >
              <ShareFat size={20} />
              Forward to Group
            </button>
            {/* Edit Entry for deposit / withdrawal */}
            {(() => {
              const msg = messages.find(m => m.id === longPressMenu.messageId);
              if (!msg) return null;

              const isCancelledMsg = msg.cancelled || (msg.text && (msg.text.includes('cancelled') || msg.text.includes('cancelled by') || msg.text.includes('❌')));
              if (isCancelledMsg) return null;

              const isWithdrawMsg = msg.message_type === 'coin_cut' || 
                (msg.text && (msg.text.includes('withdraw') || msg.text.includes('Withdraw') || msg.text.includes('Coins ₹')) && !msg.text.includes('Withdraw Form'));

              if (isWithdrawMsg && (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'deposit_handler' || user?.role === 'withdrawal_handler')) {
                return (
                  <button
                    onClick={() => {
                      setEditingWithdrawMsg(msg);
                      setShowEditWithdrawModal(true);
                      setLongPressMenu(null);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2 text-amber-600 font-semibold"
                  >
                    <PencilSimple size={20} />
                    Edit Entry
                  </button>
                );
              }

              if (msg.message_type === 'deposit' && !msg.cancelled) {
                return (
                  <button
                    onClick={() => {
                      setEditingDepositMsg(msg);
                      setLongPressMenu(null);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2 text-green-600 font-semibold"
                  >
                    <Copy size={20} />
                    Edit Entry
                  </button>
                );
              }

              return (msg.sender_id === user.id && msg.text && !msg.text.includes('🎫')) ? (
                <button
                  onClick={() => {
                    setEditingMsg(msg);
                    setEditText(msg.text || '');
                    setLongPressMenu(null);
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-wa-hover flex items-center gap-2 text-green-600"
                  data-testid="edit-message-btn"
                >
                  <Copy size={20} />
                  Edit
                </button>
              ) : null;
            })()}
          </div>
        </>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-wa-panel rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Message</h3>
            <p className="text-wa-text-secondary mb-6">Choose delete option:</p>
            <div className="space-y-2">
              <button
                disabled={isDeletingMessage}
                onClick={() => handleDeleteMessage(false)}
                className="w-full px-4 py-3 bg-wa-hover hover:bg-wa-hover rounded-lg text-left flex items-center justify-between font-medium cursor-pointer disabled:opacity-50"
              >
                <span>Delete for Me</span>
                {deletingAction === 'me' && (
                  <span className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin inline-block" />
                )}
              </button>
              <button
                disabled={isDeletingMessage}
                onClick={() => handleDeleteMessage(true)}
                className="w-full px-4 py-3 bg-wa-hover hover:bg-red-200 rounded-lg text-left text-red-600 flex items-center justify-between font-medium cursor-pointer disabled:opacity-50"
              >
                <span>Delete for Everyone</span>
                {deletingAction === 'everyone' && (
                  <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin inline-block" />
                )}
              </button>
              <button
                disabled={isDeletingMessage}
                onClick={() => {
                  setShowDeleteModal(false);
                  setMessageToDelete(null);
                }}
                className="w-full px-4 py-3 border rounded-lg hover:bg-wa-hover font-medium cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Forward Modal - Multi-select */}
      <MultiSelectForwardModal
        open={showForwardModal}
        chats={availableChats}
        accentColor="#0A6F67"
        onClose={() => {
          setShowForwardModal(false);
          setMessageToForward(null);
        }}
        onForward={forwardToChat}
      />

      {/* MediaPicker */}
      {showMediaPicker && (
        <MediaPicker
          onMediaSelected={handleMediaSelected}
          onClose={() => setShowMediaPicker(false)}
          userRole="handler"
          onOpenSavedTexts={() => setShowSavedTextsPanel(true)}
          onOpenSavedPhotos={() => setShowSavedPhotosPanel(true)}
          onOpenStickers={() => setShowStickersPanel(true)}
        />
      )}

      {/* Saved Texts Panel */}
      {showSavedTextsPanel && (
        <SavedTextsPanel
          userId={user.id}
          userRole="handler"
          onTextSelect={(text) => { sendMessage(null, text); setShowSavedTextsPanel(false); }}
          onClose={() => setShowSavedTextsPanel(false)}
        />
      )}

      {/* Stickers Panel */}
      {showStickersPanel && (
        <StickersPanel
          userId={user.id}
          userRole={user.role}
          onStickerSelect={(url) => handleMediaSelected({ type: 'sticker', url })}
          onClose={() => setShowStickersPanel(false)}
        />
      )}

      {/* Saved Photos Panel */}
      {showSavedPhotosPanel && (
        <SavedPhotosPanel
          userId={user.id}
          onPhotoSelect={(media) => { handleMediaSelected(media); setShowSavedPhotosPanel(false); }}
          onClose={() => setShowSavedPhotosPanel(false)}
        />
      )}

      {/* Image Preview Modal */}
      {showImagePreview && previewImageUrl && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          data-testid="media-viewer"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between p-4 text-white">
            <button onClick={closeImagePreview} className="p-2 hover:bg-white/10 rounded-full">
              <X size={24} weight="bold" />
            </button>
            <span className="text-sm font-medium">
              {allMediaUrls.length > 1 ? `${currentMediaIdx + 1} / ${allMediaUrls.length}` : 'Photo'}
            </span>
            <div className="w-10" />
          </div>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center px-4 relative">
            {allMediaUrls.length > 1 && currentMediaIdx > 0 && (
              <button
                onClick={() => { const idx = currentMediaIdx - 1; setCurrentMediaIdx(idx); setPreviewImageUrl(allMediaUrls[idx]); }}
                className="absolute left-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10"
              >
                <ArrowLeft size={20} weight="bold" />
              </button>
            )}
            <img 
              src={previewImageUrl} 
              alt="Preview" 
              className="max-w-full max-h-[70vh] object-contain"
            />
            {allMediaUrls.length > 1 && currentMediaIdx < allMediaUrls.length - 1 && (
              <button
                onClick={() => { const idx = currentMediaIdx + 1; setCurrentMediaIdx(idx); setPreviewImageUrl(allMediaUrls[idx]); }}
                className="absolute right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10"
              >
                <ShareFat size={20} weight="bold" />
              </button>
            )}
          </div>

          {/* Bottom Action Bar - WhatsApp Style */}
          <div className="flex items-center justify-around p-4 border-t border-white/10">
            <button
              onClick={() => { if(navigator.share) navigator.share({url: previewImageUrl}).catch(()=>{}); else { navigator.clipboard.writeText(previewImageUrl); toast('Link copied'); } }}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white"
            >
              <ShareFat size={22} />
              <span className="text-[10px]">Forward</span>
            </button>
            <button
              onClick={closeImagePreview}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white"
            >
              <ArrowLeft size={22} />
              <span className="text-[10px]">Back</span>
            </button>
            <a
              href={previewImageUrl}
              download="photo.jpg"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white"
            >
              <CaretDown size={22} weight="bold" />
              <span className="text-[10px]">Download</span>
            </a>
          </div>
        </div>
      )}

      {/* Register Customer Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-wa-text-primary mb-4">Register Customer</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">Name</label>
                <input
                  type="text"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={registerForm.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 10) setRegisterForm({...registerForm, phone: val});
                  }}
                  className="w-full px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="10-digit mobile number"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-wa-text-primary mb-1">Password</label>
                <input
                  type="text"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  className="w-full px-3 py-2 border border-wa-border bg-wa-hover text-wa-text-primary placeholder:text-wa-text-secondary rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Default: Abcd1234"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setShowRegisterModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-wa-hover text-wa-text-primary"
              >
                Cancel
              </button>
              <button
                onClick={handleRegisterCustomer}
                disabled={registering}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold transition-all"
              >
                {registering ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                    <span>Registering...</span>
                  </>
                ) : (
                  'Register'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Client Modal */}
      {showEditModal && editClientData && (
        <EditClientModal
          client={editClientData}
          isRegistered={true}
          userId={user?.id}
          userRole={user?.role}
          onClose={() => {
            setShowEditModal(false);
            setEditClientData(null);
            const loadUpdated = async () => {
              try {
                const roomResponse = await axios.get(`${API}/chat/room/${roomId}`);
                const room = roomResponse.data;
                if (room) {
                  setCustomer(prev => ({
                    ...prev,
                    id: room.customer_id,
                    name: room.customer_name,
                    phone: room.customer_phone,
                    profile_pic: room.customer_profile_pic,
                    is_guest: room.customer_is_guest || false,
                    role: room.customer_role || 'customer',
                    mb_setup: room.mb_setup || prev?.mb_setup || null,
                    is_suspicious: room.is_suspicious || false,
                    flag_reason: room.flag_reason || '',
                    is_vip: room.is_vip || false,
                    chat_type: room.chat_type || prev?.chat_type || '',
                    address: room.customer_address || room.address || prev?.address || ''
                  }));
                }
              } catch (e) { console.error(e); }
            };
            loadUpdated();
          }}
        />
      )}

      {/* Forward to Group Modal */}
      {showForwardGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg max-w-sm w-full max-h-[70vh] overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="text-lg font-bold text-wa-text-primary">Forward to Group</h3>
            </div>
            <div className="overflow-y-auto max-h-[50vh]">
              {availableGroups.length === 0 ? (
                <p className="p-4 text-wa-text-secondary text-center">No groups available</p>
              ) : (
                availableGroups.map(group => (
                  <button
                    key={group.id}
                    onClick={() => forwardToGroup(group.id)}
                    className="w-full px-4 py-3 text-left hover:bg-wa-hover border-b flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-wa-hover flex items-center justify-center text-wa-text-primary font-bold">
                      {(group.name || 'G')[0]}
                    </div>
                    <span className="font-medium text-wa-text-primary">{group.name}</span>
                  </button>
                ))
              )}
            </div>
            <div className="p-3 border-t">
              <button
                onClick={() => { setShowForwardGroupModal(false); setForwardGroupMsg(null); }}
                className="w-full px-4 py-2 border rounded-lg hover:bg-wa-hover text-wa-text-primary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Message Modal */}
      {editingMsg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-xl max-w-sm w-full p-5 shadow-2xl" data-testid="edit-message-modal">
            <h3 className="text-lg font-bold text-wa-text-primary mb-3">Edit Message</h3>
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2 border border-wa-border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm min-h-[80px] resize-none"
              autoFocus
              disabled={isSavingEdit}
              data-testid="edit-message-input"
            />
            <div className="flex gap-3 mt-4">
              <button
                disabled={isSavingEdit}
                onClick={() => { setEditingMsg(null); setEditText(''); setIsSavingEdit(false); }}
                className="flex-1 px-4 py-2.5 border border-wa-border rounded-lg text-wa-text-primary hover:bg-wa-hover disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                disabled={isSavingEdit}
                onClick={async () => {
                  if (!editText.trim()) { toast.error('Message cannot be empty'); return; }
                  setIsSavingEdit(true);
                  try {
                    await axios.put(`${API}/chat/message/${editingMsg.id}/edit`, {
                      user_id: user.id,
                      text: editText.trim()
                    });
                    setMessages(prev => prev.map(m => m.id === editingMsg.id ? { ...m, text: editText.trim(), edited: true } : m));
                    toast.success('Message edited');
                    setEditingMsg(null);
                    setEditText('');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to edit');
                  } finally {
                    setIsSavingEdit(false);
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-[#00a884] text-white rounded-lg hover:bg-[#008f72] disabled:opacity-75 flex items-center justify-center gap-2 font-medium"
                data-testid="confirm-edit-btn"
              >
                {isSavingEdit ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Call Screen */}
      {activeCall && (
        <AudioCallScreen
          socket={socket}
          callId={activeCall.callId}
          callerId={activeCall.callerId}
          calleeId={activeCall.calleeId}
          callerName={activeCall.callerName}
          calleeName={activeCall.calleeName}
          callerProfilePic={activeCall.callerProfilePic}
          calleeProfilePic={activeCall.calleeProfilePic}
          isIncoming={activeCall.isIncoming}
          currentUserId={user.id}
          currentUserRole="handler"
          isConference={activeCall.isConference || false}
          onClose={() => setActiveCall(null)}
          API={API}
        />
      )}

      {/* Incoming Call Overlay */}
      {incomingCall && !activeCall && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#1a3a2a] rounded-2xl p-8 max-w-xs w-full text-center" data-testid="incoming-call-modal">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] mx-auto mb-4 flex items-center justify-center overflow-hidden">
              {incomingCall.callerProfilePic ? (
                <img src={`${API}${incomingCall.callerProfilePic}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-white">{(incomingCall.callerName || 'U')[0]?.toUpperCase()}</span>
              )}
            </div>
            <h3 className="text-xl font-bold text-white mb-1">{incomingCall.callerName}</h3>
            <p className="text-white/50 text-sm mb-6">
              {incomingCall.isConference 
                ? `Conference call (${incomingCall.participantNames?.join(', ')})` 
                : 'Incoming audio call...'}
            </p>
            <div className="flex justify-around">
              <button
                onClick={() => {
                  socket.emit('call_reject', { call_id: incomingCall.callId });
                  setIncomingCall(null);
                }}
                className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center"
                data-testid="reject-incoming-btn"
              >
                <Phone size={24} weight="fill" className="text-white rotate-[135deg]" />
              </button>
              <button
                onClick={() => {
                  setActiveCall({
                    callId: incomingCall.callId,
                    callerId: incomingCall.callerId,
                    calleeId: user.id,
callerName: incomingCall.callerName,
                    calleeName: user.username,
                    callerProfilePic: incomingCall.callerProfilePic,
                    calleeProfilePic: user.profile_pic,
                    isIncoming: true,
                    isConference: incomingCall.isConference || false
                  });
                  setIncomingCall(null);
                }}
                className="w-14 h-14 rounded-full bg-[#00a884] flex items-center justify-center"
                data-testid="accept-incoming-btn"
              >
                <Phone size={24} weight="fill" className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Request Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="p-1 text-white bg-[#e11d48] rounded font-bold text-xs">SOS</span>
                <h3 className="font-bold text-gray-800 text-base">Support Request</h3>
              </div>
              <button 
                onClick={() => setShowSupportModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const query = e.target.queryText.value;
              handleSupportSubmit(query);
            }} className="p-5">
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Query / Message (required)
                </label>
                <textarea
                  name="queryText"
                  required
                  rows={4}
                  placeholder="Describe your issue..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSupportModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingSupport}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#9333ea] rounded-lg hover:bg-[#7e22ce] transition-colors disabled:opacity-50"
                >
                  {isSubmittingSupport ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark as Solved Modal */}
      {showMarkSolvedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="p-1 text-emerald-600 bg-emerald-50 rounded font-bold text-sm">✓</span>
                <h3 className="font-bold text-gray-800 text-base">Mark as Solved</h3>
              </div>
              <button 
                onClick={() => setShowMarkSolvedModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const note = e.target.noteText.value;
              handleMarkSolvedSubmit(note);
            }} className="p-5">
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1.5">
                  Add a note (optional) — will show in the Enquiry report.
                </label>
                <textarea
                  name="noteText"
                  rows={3}
                  placeholder="e.g. Client converted, MB assigned"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowMarkSolvedModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingMarkSolved}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {isSubmittingMarkSolved ? 'Processing...' : 'Mark Solved'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditWithdrawModal && (
        <EditWithdrawModal
          isOpen={showEditWithdrawModal}
          onClose={() => {
            setShowEditWithdrawModal(false);
            setEditingWithdrawMsg(null);
          }}
          msg={editingWithdrawMsg}
          customer={customer}
          roomMessages={messages}
          pendingRequest={selectedPendingRequest}
          onSave={handleSaveEditWithdraw}
        />
      )}

      {showPendingWithdrawRequestsModal && (
        <PendingWithdrawRequestsModal
          isOpen={showPendingWithdrawRequestsModal}
          onClose={() => setShowPendingWithdrawRequestsModal(false)}
          roomId={roomId}
          onApprove={(req) => {
            setSelectedPendingRequest(req);
            setShowPendingWithdrawRequestsModal(false);
            setShowCoinCutModal(true);
          }}
          onCancel={handleCancelRequest}
        />
      )}

      {showCreateWithdrawFormModal && (
        <CreateWithdrawFormModal
          isOpen={showCreateWithdrawFormModal}
          onClose={() => setShowCreateWithdrawFormModal(false)}
          customer={customer}
          user={user}
          onSend={(amount, reference) => {
            handleSendCreateWithdrawForm(amount, reference);
            setShowCreateWithdrawFormModal(false);
          }}
        />
      )}

      {/* Deposit Modal */}
      {showPendingRequestsModal && (
        <PendingRequestsModal
          isOpen={showPendingRequestsModal}
          onClose={() => setShowPendingRequestsModal(false)}
          roomId={roomId}
          onApprove={(req) => {
            setSelectedPendingRequest(req);
            setShowPendingRequestsModal(false);
            setShowDepositModal(true);
          }}
          onCancel={handleCancelRequest}
        />
      )}

      {cancelRemarkTarget && (
        <CancelRemarkModal
          isOpen={Boolean(cancelRemarkTarget)}
          onClose={() => setCancelRemarkTarget(null)}
          onSubmit={handleConfirmCancelWithRemark}
          request={cancelRemarkTarget}
        />
      )}

      {/* Deposit Modal */}
      {(showDepositModal || editingDepositMsg) && (
        <DepositModal
          isOpen={Boolean(showDepositModal || editingDepositMsg)}
          onClose={() => {
            setShowDepositModal(false);
            setEditingDepositMsg(null);
            setSelectedPendingRequest(null);
          }}
          customer={customer}
          editingMsg={editingDepositMsg}
          pendingRequest={selectedPendingRequest}
          roomMessages={messages}
          onSend={async (formattedMessage, txData) => {
            if (selectedPendingRequest) {
              try {
                await axios.post(`${API}/chat/form-request/${selectedPendingRequest.id}/approve`, {
                  amount: parseFloat(txData.amount),
                  utr: txData.utr,
                  received_from: txData.receivedFrom,
                  received_in: txData.receivedIn,
                  description: txData.description
                });
                toast.success('Deposit approved successfully');
                setSelectedPendingRequest(null);
                setShowDepositModal(false);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to approve');
              }
              return;
            }

            if (editingDepositMsg) {
              try {
                const recFromLine = (txData.receivedFrom && txData.receivedFrom.trim()) ? `\nReceived From: ${txData.receivedFrom.trim()}` : '';
                const recInLine = (txData.receivedIn && txData.receivedIn.trim()) ? `\nReceived In: ${txData.receivedIn.trim()}` : '';
                const displayAmount = txData.amount ? txData.amount : '';
                const updatedMsg = `✅ आपके ID में ${displayAmount} Coins सफलतापूर्वक जमा कर दिए गए हैं। कृपया अपनी ID को Refresh करके चेक कर लें。\nUTR: ${txData.utr || ''}${recFromLine}${recInLine}\n✅ ${displayAmount} Coins have been successfully deposited into your ID. Please refresh your ID and check.`;
                
                await axios.put(`${API}/chat/message/${editingDepositMsg.id}/edit`, {
                  user_id: user.id,
                  text: updatedMsg,
                  amount: txData.amount ? parseFloat(txData.amount) : null,
                  utr: txData.utr || null,
                  received_from: txData.receivedFrom || null,
                  received_in: txData.receivedIn || null,
                  description: txData.description || null,
                  form_id: txData.formNo || null,
                  form_no: txData.formNo || null,
                  txId: txData.txId || null
                });
                
                setMessages(prev => prev.map(m => m.id === editingDepositMsg.id ? {
                  ...m,
                  text: updatedMsg,
                  edited: true,
                  amount: txData.amount ? parseFloat(txData.amount) : null,
                  utr: txData.utr || null,
                  received_from: txData.receivedFrom || null,
                  received_in: txData.receivedIn || null,
                  description: txData.description || null
                } : m));
                toast.success('Deposit edited successfully');

                if (txData.oldAmount && parseFloat(txData.oldAmount) !== parseFloat(txData.amount)) {
                  const reqNum = txData.formNo || (txData.txId ? ('D' + String(txData.txId).padStart(3, '0')) : '');
                  const reqLabel = reqNum ? ` request ${reqNum}` : ' request';
                  const notificationMsg = `🔔 आपका${reqLabel} एक गलती की वजह से हमें update करना पड़ा। Amount ${txData.oldAmount} से बदलकर ${txData.amount} कर दिया गया है। अगर कोई concern हो तो हमारे support person से contact करें।`;
                  await sendMessage(null, notificationMsg);
                }
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to edit');
              }
              setEditingDepositMsg(null);
            } else {
              try {
                await axios.post(`${API}/chat/form-request/create-and-approve`, {
                  roomId: roomId,
                  amount: parseFloat(txData.amount),
                  utr: txData.utr,
                  received_from: txData.receivedFrom,
                  received_in: txData.receivedIn,
                  description: txData.description
                });
                toast.success('Deposit created and approved successfully');
                setShowDepositModal(false);
              } catch (err) {
                console.error("Failed to submit deposit:", err);
                toast.error(err.response?.data?.error || err.response?.data?.detail || "Failed to create & approve deposit");
              }
            }
          }}
        />
      )}

{showCreateDepositFormModal && (
  <CreateDepositFormModal
    isOpen={showCreateDepositFormModal}
    onClose={() => {
      setShowCreateDepositFormModal(false);
      setIsCreateAndApproveFlow(false);
    }}
    customer={customer}
    user={user}
    title={isCreateAndApproveFlow ? "Create deposit and Approve Deposit" : undefined}
    submitting={createDepositSubmitting}
    onSend={async (amountVal, refText) => {
      setCreateDepositSubmitting(true);
      try {
        const res = await axios.post(`${API}/chat/form-request/create`, {
          type: 'deposit',
          amount: parseFloat(amountVal),
          roomId: roomId,
          description: refText || null
        });
        const formRequest = res.data;

        // Check if there was a recent unhandled customer request
        let hasCustomerRequest = false;
        for (let i = messages.length - 1; i >= 0; i--) {
          const m = messages[i];
          if (m.text && m.text.includes("— Deposit Form")) {
            break;
          }
          if (m.text && (
            m.text.toLowerCase().includes("hi, i want despoit") ||
            m.text.toLowerCase().includes("hi, i want deposit")
          ) && m.sender_role === 'customer') {
            hasCustomerRequest = true;
            break;
          }
        }

        const clientName = customer?.username || customer?.name || 'client';
        const clientTag = `@${clientName}`;

        let requesterStr = '';
        if (hasCustomerRequest) {
          requesterStr = `Requested by: ${clientName} (client)\n${clientTag}`;
        } else {
          const isSuperAdmin = user?.role === 'super_admin';
          const isAdmin = user?.role === 'admin';
          const roleLabel = (isSuperAdmin || isAdmin) ? 'admin' : 'deposit handler';
          const staffName = user?.username || user?.name || 'admin';
          requesterStr = `Requested by: ${staffName} (${roleLabel})\n${clientTag}`;
        }

        const formNo = formRequest?.form_id || 'D001';
        const formattedMessage = `🎫 ${formNo} — Deposit Form\n${requesterStr}\nAmount: ₹${amountVal}${refText?.trim() ? `\nReference: ${refText.trim()}` : ''}`;
        await sendMessage(null, formattedMessage, { message_type: 'text', as_customer: true });
        toast.success("Deposit form request created!");

        setShowCreateDepositFormModal(false);
        if (isCreateAndApproveFlow) {
          setIsCreateAndApproveFlow(false);
          setShowPendingRequestsModal(true);
        }
      } catch (err) {
        console.error("Failed to create deposit form request", err);
        toast.error(err.response?.data?.error || "Failed to create deposit request");
      } finally {
        setCreateDepositSubmitting(false);
      }
    }}
  />
)}
      {/* Coin Cut Modal */}
      {(showCoinCutModal || editingCoinCutMsg) && (
        <CoinCutModal
          isOpen={Boolean(showCoinCutModal || editingCoinCutMsg)}
          onClose={() => {
            setShowCoinCutModal(false);
            setEditingCoinCutMsg(null);
            setSelectedPendingRequest(null);
          }}
          customer={customer}
          roomId={roomId}
          editingMsg={editingCoinCutMsg}
          pendingRequest={selectedPendingRequest}
          onSend={async (formattedMessage, txData) => {
            if (editingCoinCutMsg) {
              try {
                await axios.put(`${API}/chat/message/${editingCoinCutMsg.id}/edit`, {
                  user_id: user.id,
                  text: formattedMessage,
                  amount: txData.amount ? parseFloat(txData.amount) : null,
                  utr: txData.utr || null,
                  paid_to: txData.paid_to || null,
                  paid_from: txData.paid_from || null,
                  description: txData.description || null
                });
                setMessages(prev => prev.map(m => m.id === editingCoinCutMsg.id ? {
                  ...m,
                  text: formattedMessage,
                  edited: true,
                  amount: txData.amount ? parseFloat(txData.amount) : null,
                  utr: txData.utr || null,
                  paid_to: txData.paid_to || null,
                  paid_from: txData.paid_from || null,
                  description: txData.description || null
                } : m));
                toast.success('Coin cut edited successfully');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to edit');
              }
              setEditingCoinCutMsg(null);
            } else {
              if (selectedPendingRequest) {
                try {
                  await axios.post(`${API}/chat/form-request/${selectedPendingRequest.id}/approve`, {
                    amount: txData.amount ? parseFloat(txData.amount) : 0,
                    utr: txData.utr || null
                  });
                } catch (approveErr) {
                  console.error("Failed to mark form_request as closed on coin cut:", approveErr);
                }
                await sendMessage(null, formattedMessage, {
                  message_type: 'coin_cut',
                  amount: txData.amount ? parseFloat(txData.amount) : null,
                  utr: txData.utr || null,
                  form_no: selectedPendingRequest?.form_id || txData.formNo || null,
                  paid_to: txData.paid_to || null,
                  paid_from: txData.paid_from || null,
                  description: txData.description || null,
                  bank_name: txData.bank_name || null,
                  bank_account_number: txData.bank_account_number || null
                });
                setShowCoinCutModal(false);
                setSelectedPendingRequest(null);
                window.dispatchEvent(new CustomEvent('refresh-header-badges'));
              } else {
                try {
                  await axios.post(`${API}/chat/form-request/create-and-cut`, {
                    roomId: roomId,
                    amount: parseFloat(txData.amount),
                    description: txData.description,
                    bank_name: txData.bank_name,
                    bank_account_number: txData.bank_account_number,
                    account_holder_name: txData.account_holder_name,
                    ifsc_code: txData.ifsc_code,
                    upi_id: txData.upi_id
                  });
                  toast.success('Coin cut successfully');
                  setShowCoinCutModal(false);
                  window.dispatchEvent(new CustomEvent('refresh-header-badges'));
                } catch (err) {
                  console.error("Failed to submit coin cut:", err);
                  toast.error(err.response?.data?.error || err.response?.data?.detail || "Failed to process coin cut");
                }
              }
            }
          }}
        />
      )}
      {/* Cancel Deposit Modal */}
      {cancellingMsg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl animate-in fade-in zoom-in-95 duration-150 text-left">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Cancel this deposit?</h3>
            <p className="text-xs text-gray-500 mb-4">Customer will see this reason. Amount will be reversed.</p>
            
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Wrong customer, duplicate entry, bank mismatch"
              className="w-full h-24 p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm mb-4 resize-none"
            />
            
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setCancellingMsg(null);
                  setCancelReason('');
                }}
                className="px-4 py-2 border border-gray-300 hover:bg-gray-50 rounded-xl text-sm font-semibold text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCancelDeposit}
                disabled={!cancelReason.trim()}
                className="px-4 py-2 bg-[#d32f2f] hover:bg-[#b71c1c] text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
              >
                Cancel deposit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// EditWithdrawModal Component (matching Img 4 & Img 5 specification)
const EditWithdrawModal = ({ isOpen, onClose, msg, customer, roomMessages, pendingRequest, onSave }) => {
  const [submitting, setSubmitting] = useState(false);
  const [oldAmount, setOldAmount] = useState('');
  const [oldReference, setOldReference] = useState('');
  const [oldUtr, setOldUtr] = useState('');
  const [oldPaidRef, setOldPaidRef] = useState('');
  const [oldPaidBank, setOldPaidBank] = useState('');

  const [newAmount, setNewAmount] = useState('');
  const [newReference, setNewReference] = useState('');
  const [newUtr, setNewUtr] = useState('');
  const [newPaidRef, setNewPaidRef] = useState('');
  const [newPaidBank, setNewPaidBank] = useState('');

  useEffect(() => {
    if (isOpen && msg) {
      const text = msg.text || '';
      const amtMatch = text.match(/Amount:\s*₹?\s*([0-9.]+)/i) || text.match(/Coins ₹\s*([0-9.]+)/i);
      const amt = msg.amount ? msg.amount.toString() : (amtMatch ? amtMatch[1] : '0');

      const utrMatch = text.match(/UTR\s*(?:Number)?:?\s*([^\n]+)/i);
      const utr = msg.utr || (utrMatch ? utrMatch[1].trim() : '');

      const refMatch = text.match(/Ref:\s*([^\n]+)/i) || text.match(/Reference:\s*([^\n]+)/i);
      const ref = msg.description || (refMatch ? refMatch[1].trim() : '');

      const paidFromMatch = text.match(/Paid From:\s*([^\n]+)/i);
      const rawPaidFrom = msg.paid_from || (paidFromMatch ? paidFromMatch[1].trim() : '');
      const paidFrom = (rawPaidFrom && !rawPaidFrom.toLowerCase().includes('iss bank account')) ? rawPaidFrom : '';

      setOldAmount(amt);
      setNewAmount(amt);

      setOldReference(ref);
      setNewReference(ref);

      setOldUtr(utr);
      setNewUtr(utr);

      setOldPaidRef('');
      setNewPaidRef('');

      setOldPaidBank(paidFrom);
      setNewPaidBank(paidFrom);
    }
  }, [isOpen, msg]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const text = msg?.text || msg?.message_text || '';
      let rawForm = msg?.form_no || msg?.formNo || msg?.form_id || msg?.formId || msg?.reference || '';
      if (!rawForm) {
        const formMatch = text.match(/(W\d+)/i) || text.match(/Form\s*(?:No|#)?:?\s*([W\d]+)/i) || text.match(/request\s+([W\d]+)/i);
        if (formMatch) rawForm = formMatch[1];
      }
      if (!rawForm && Array.isArray(roomMessages)) {
        let msgIdx = roomMessages.length - 1;
        if (msg && (msg.id || msg.message_id)) {
          const idx = roomMessages.findIndex(m => String(m.id || m.message_id) === String(msg.id || msg.message_id));
          if (idx !== -1) msgIdx = idx;
        }
        for (let i = msgIdx; i >= 0; i--) {
          const m = roomMessages[i];
          if (m?.form_id || m?.form_no) {
            rawForm = m.form_id || m.form_no;
            break;
          }
          const mText = m?.text || m?.message_text || m?.media?.message_text || '';
          const match = mText.match(/(W\d+)/i) || mText.match(/Form\s*(?:No|#)?:?\s*([W\d]+)/i);
          if (match) {
            rawForm = match[1];
            break;
          }
        }
      }
      if (!rawForm && pendingRequest?.form_id) {
        rawForm = pendingRequest.form_id;
      }
      let formId = 'W1';
      if (rawForm) {
        const clean = String(rawForm).trim().toUpperCase();
        formId = clean.startsWith('W') ? clean : `W${clean}`;
      }

      await onSave({
        formId,
        oldAmount,
        newAmount,
        newReference,
        newUtr,
        newPaidRef,
        newPaidBank
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[110] p-4 backdrop-blur-[2px]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-gray-200 overflow-hidden animate-fade-in text-gray-800">
        {/* Header */}
        <div className="bg-[#f59e0b] px-5 py-3.5 flex items-center justify-between text-white font-bold">
          <div className="flex items-center gap-2 text-base uppercase tracking-wide">
            <span>✏️</span>
            <span>EDIT WITHDRAW</span>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/20 rounded-full text-white cursor-pointer">
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column: Old Values */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">पुरानी VALUE</h4>
              <div>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={oldAmount}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 text-sm"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">Amount (₹)</span>
              </div>
              <div>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={oldReference || 'plz'}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 text-sm"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">Reference</span>
              </div>
              <div>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={oldUtr}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 text-sm"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">UTR Number</span>
              </div>
              <div>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={oldPaidRef || '(empty)'}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 text-sm"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">Paid Reference</span>
              </div>
              <div>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={oldPaidBank}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500 text-sm"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">Paid Bank Account</span>
              </div>
            </div>

            {/* Right Column: New Values */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-[#d97706] uppercase tracking-wider">नई VALUE</h4>
              <div>
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-[11px] text-gray-500 mt-0.5 block">Amount (₹)</span>
              </div>
              <div>
                <input
                  type="text"
                  value={newReference}
                  onChange={(e) => setNewReference(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-[11px] text-gray-500 mt-0.5 block">Reference</span>
              </div>
              <div>
                <input
                  type="text"
                  value={newUtr}
                  onChange={(e) => setNewUtr(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-[11px] text-gray-500 mt-0.5 block">UTR Number</span>
              </div>
              <div>
                <input
                  type="text"
                  value={newPaidRef}
                  onChange={(e) => setNewPaidRef(e.target.value)}
                  placeholder="Paid Reference"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-[11px] text-gray-500 mt-0.5 block">Paid Reference</span>
              </div>
              <div>
                <input
                  type="text"
                  value={newPaidBank}
                  onChange={(e) => setNewPaidBank(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <span className="text-[11px] text-gray-500 mt-0.5 block">Paid Bank Account</span>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 hover:bg-gray-50 rounded-xl font-semibold text-sm text-gray-700 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting}
              className="px-6 py-2 bg-[#fcd34d] hover:bg-[#fbbf24] disabled:opacity-50 text-gray-900 font-bold rounded-xl text-sm transition-colors shadow-xs cursor-pointer"
            >
              {submitting ? '⏳ Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// CreateWithdrawFormModal Component
const CreateWithdrawFormModal = ({ isOpen, onClose, customer, onSend, user }) => {
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!amount) return;
    onSend(amount, reference);
  };

  const roleLabel = user?.role === 'super_admin' ? 'admin' : (user?.role || 'handler');
  const staffName = user?.username || user?.name || 'admin';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-gray-200 overflow-hidden animate-fade-in text-gray-800">
        <div className="bg-[#8b5cf6] px-5 py-4 flex items-center justify-between text-white font-bold">
          <div className="flex items-center gap-2 text-base">
            <span>💳</span>
            <span>Create Withdraw Request for {customer?.username || customer?.name || 'Customer'}</span>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/10 rounded-full text-white">
            <X size={18} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Amount (₹)</label>
            <input
              type="number"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5000"
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Reference (optional)</label>
            <textarea
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="witg"
              rows={2}
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm resize-none"
            />
          </div>
          <p className="text-[11px] text-gray-500 italic">
            "Requested by: {staffName} ({roleLabel})" chat card mein show hoga.
          </p>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg font-semibold text-xs text-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs transition-colors shadow-xs"
            >
              Create Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Pending Withdraw Requests Modal Component
const PendingWithdrawRequestsModal = ({ isOpen, onClose, roomId, onApprove, onCancel }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);

  const handleApproveAction = async (req) => {
    if (submittingId) return;
    setSubmittingId(req.id);
    try {
      await onApprove(req);
    } finally {
      setSubmittingId(null);
    }
  };

  const handleCancelAction = async (req) => {
    if (submittingId) return;
    setSubmittingId(req.id);
    try {
      await onCancel(req);
    } finally {
      setSubmittingId(null);
    }
  };

  const loadRequests = async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chat/room/${roomId}/pending-requests?type=withdrawal`);
      setRequests(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load pending withdraw requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRequests();
    }
  }, [isOpen, roomId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-gray-200 overflow-hidden animate-fade-in text-gray-800">
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-gray-100 font-bold text-gray-900">
          <div className="flex items-center gap-2 text-base">
            <span className="text-pink-500">🎀</span>
            <span>Pending Withdraw Requests</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="bg-gray-50 text-[11px] text-gray-500 px-5 py-2 border-b border-gray-100 font-medium leading-tight">
          Pick one — Approve to send money, or Cancel with a reason. Drag the top bar to reposition.
        </div>

        {/* Body */}
        <div className="p-4 max-h-[300px] overflow-y-auto space-y-3">
          {loading ? (
            <p className="text-center text-xs text-gray-400 py-4">Loading...</p>
          ) : requests.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-4">No pending withdraw requests.</p>
          ) : (
            requests.map((req) => {
              const acctDisplay = req.bank_account_number ? `${req.bank_name || 'Bank'} · A/c ${req.bank_account_number}` : (req.upi_id || 'Account details pending');
              const isSubmittingThis = submittingId === req.id;
              return (
                <div key={req.id} className="border border-gray-200 bg-gray-50/50 p-4 rounded-xl space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-sm text-gray-900">{req.form_id}</span>
                    <span className="text-[10px] text-gray-400 uppercase font-semibold">
                      WITHDRAW
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    Account: <strong className="text-gray-800">{acctDisplay}</strong>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handleApproveAction(req)}
                      disabled={Boolean(submittingId)}
                      className="flex-1 py-2 bg-[#00a884] hover:bg-[#008f70] disabled:opacity-50 text-white font-bold rounded-lg text-xs transition-colors flex items-center justify-center gap-1 shadow-xs cursor-pointer"
                    >
                      {isSubmittingThis ? '⏳ ...' : '✓ Approve'}
                    </button>
                    <button
                      onClick={() => handleCancelAction(req)}
                      disabled={Boolean(submittingId)}
                      className="flex-1 py-2 bg-[#dc2626] hover:bg-[#b91c1c] disabled:opacity-50 text-white font-bold rounded-lg text-xs transition-colors flex items-center justify-center gap-1 shadow-xs cursor-pointer"
                    >
                      {isSubmittingThis ? '⏳ ...' : '✕ Cancel'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// CreateDepositFormModal Component
const CreateDepositFormModal = ({ isOpen, onClose, customer, onSend, user, title, submitting }) => {
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || submitting) return;
    await onSend(amount, reference);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px]">
      <div className="bg-wa-panel rounded-xl shadow-2xl max-w-sm w-full border border-wa-border overflow-hidden animate-fade-in animate-duration-200 text-wa-text-primary">
        <div className="bg-purple-600 px-4 py-3 flex items-center justify-between text-white font-semibold shadow-sm">
          <div className="flex items-center gap-2">
            <span>🎫</span>
            <span>{title || `Create Deposit Request for ${customer?.name || 'Customer'}`}</span>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} className="p-1 hover:bg-white/10 rounded-full text-white disabled:opacity-50">
            <X size={18} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-wa-text-secondary mb-1">Amount (₹)</label>
            <input
              type="number"
              required
              disabled={submitting}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 5000"
              className="w-full px-3 py-2 border rounded-lg bg-wa-panel border-wa-border text-wa-text-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-wa-text-secondary mb-1">Reference (optional)</label>
            <textarea
              value={reference}
              disabled={submitting}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Note or reference"
              rows={2}
              className="w-full px-3 py-2 border rounded-lg bg-wa-panel border-wa-border text-wa-text-primary focus:outline-none resize-none disabled:opacity-50"
            />
          </div>
          <p className="text-[10px] text-wa-text-secondary italic">
            *Requested by: {user?.username || 'admin'}.
          </p>
          <div className="flex justify-end gap-2 pt-2 border-t border-wa-border">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-wa-border bg-wa-panel rounded-lg font-semibold text-sm hover:bg-wa-hover text-wa-text-primary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold text-sm flex items-center gap-2"
            >
              {submitting && <CircleNotch size={16} className="animate-spin" />}
              {submitting ? 'Creating...' : 'Create Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// DepositModal Component
const DepositModal = ({ isOpen, onClose, customer, editingMsg, pendingRequest, roomMessages, onSend }) => {
  const [amount, setAmount] = useState('');
  const [utr, setUtr] = useState('');
  const [receivedFrom, setReceivedFrom] = useState('');
  const [receivedIn, setReceivedIn] = useState('');
  const [description, setDescription] = useState('');
  
  // Loader & Submitting States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // States for holding previous edit values
  const [oldAmount, setOldAmount] = useState('');
  const [oldUtr, setOldUtr] = useState('');
  const [oldDescription, setOldDescription] = useState('');
  const [oldReceivedIn, setOldReceivedIn] = useState('');
  const [txId, setTxId] = useState(null);
  const [formNo, setFormNo] = useState('');

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateIdx, setSelectedTemplateIdx] = useState(0);

  // Duplicate UTR states
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [showDuplicateTrail, setShowDuplicateTrail] = useState(false);

  const formatTrailDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  };

  // Edit History states
  const [editHistory, setEditHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const checkDuplicateUtr = async (val) => {
    if (!val || val.trim().length === 0) {
      setDuplicateMatches([]);
      return [];
    }
    try {
      const res = await axios.get(`${API}/chat/transactions/utr/${val.trim()}`);
      const filtered = res.data.filter(m => !editingMsg || m.message_id !== editingMsg.id);
      setDuplicateMatches(filtered);
      return filtered;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  useEffect(() => {
    if (isOpen) {
      setDuplicateMatches([]);
      setShowDuplicateTrail(false);
      setEditHistory([]);
      setShowHistoryModal(false);
      setIsSubmitting(false);

      if (editingMsg) {
        setIsLoadingData(true);
        
        // Initial text parsing directly from editingMsg
        const text = editingMsg.text || editingMsg.message_text || editingMsg.media?.message_text || '';
        let initialFormNo = editingMsg.form_id || editingMsg.form_no || '';
        if (!initialFormNo) {
          const formMatch = text.match(/([D]\d+)/i) || text.match(/Form\s*(?:No|#)?:?\s*([D\d]+)/i);
          if (formMatch) initialFormNo = formMatch[1].toUpperCase();
        }
        if (initialFormNo) setFormNo(initialFormNo);

        const utrMatch = text.match(/UTR:\s*([^\n\r]+)/i);
        if (utrMatch) {
          setUtr(utrMatch[1].trim());
          setOldUtr(utrMatch[1].trim());
        }
        const receivedFromMatch = text.match(/Received From:\s*([^\n\r]+)/i);
        if (receivedFromMatch) {
          const val = receivedFromMatch[1].trim();
          if (!val.startsWith('✅')) {
            setReceivedFrom(val);
          } else {
            setReceivedFrom('');
          }
        } else {
          setReceivedFrom('');
        }

        const receivedInMatch = text.match(/Received In:\s*([^\n\r]+)/i);
        if (receivedInMatch) {
          const val = receivedInMatch[1].trim();
          if (!val.startsWith('✅')) {
            setReceivedIn(val);
            setOldReceivedIn(val);
          } else {
            setReceivedIn('');
            setOldReceivedIn('');
          }
        } else {
          setReceivedIn('');
          setOldReceivedIn('');
        }
        const amountMatch = text.match(/₹?\s*([0-9,.]+)\s*सफलतापूर्वक/i) || text.match(/Amount:\s*₹?\s*([0-9,.]+)/i);
        if (amountMatch) {
          const parsedAmt = amountMatch[1].replace(/,/g, '');
          setAmount(parsedAmt);
          setOldAmount(parsedAmt);
        }

        // Fetch full transaction details from backend
        const fetchTx = async () => {
          try {
            const res = await axios.get(`${API}/chat/transaction/message/${editingMsg.id}`);
            if (res.data) {
              const tx = res.data;
              if (tx.id) {
                setTxId(tx.id);
                // Load history
                try {
                  const histRes = await axios.get(`${API}/chat/transaction/${tx.id}/history`);
                  setEditHistory(histRes.data);
                } catch (histErr) {
                  console.error(histErr);
                }
              }
              if (tx.form_no) setFormNo(tx.form_no);
              if (tx.amount) {
                setAmount(tx.amount.toString());
                setOldAmount(tx.amount.toString());
              }
              if (tx.utr) {
                setUtr(tx.utr);
                setOldUtr(tx.utr);
              }
              if (tx.received_from) setReceivedFrom(tx.received_from);
              if (tx.received_in) {
                setReceivedIn(tx.received_in);
                setOldReceivedIn(tx.received_in);
              }
              setDescription(tx.description || '');
              setOldDescription(tx.description || '');
            }
          } catch (err) {
            console.error('Failed to fetch transaction for edit:', err);
          } finally {
            setIsLoadingData(false);
          }
        };
        fetchTx();
      } else if (pendingRequest) {
        setIsLoadingData(false);
        setAmount('');
        setUtr('');
        setReceivedFrom('');
        setReceivedIn('');
        setDescription('');
        setOldAmount('');
        setOldUtr('');
        setOldReceivedIn('');
        setOldDescription('');
        setTxId(null);
        setFormNo(pendingRequest.form_id || '');
      } else {
        setIsLoadingData(false);
        setAmount('');
        setUtr('');
        setReceivedFrom('');
        setReceivedIn('');
        setDescription('');
        setOldAmount('');
        setOldUtr('');
        setOldReceivedIn('');
        setOldDescription('');
        setTxId(null);
        setFormNo('');
      }

      const load = async () => {
        try {
          const seg = customer?.mb_setup || 'ALL MB';
          const res = await axios.get(`${API}/mb-config/bot-settings?segment=${seg}`);
          const raw = res.data.template || '';
          if (raw.trim().startsWith('{')) {
            const parsed = JSON.parse(raw);
            setTemplates(parsed.client_templates || []);
          } else {
            setTemplates(raw ? [{ text: raw }] : []);
          }
        } catch (e) {
          console.error(e);
        }
      };
      load();
    }
  }, [isOpen, customer, editingMsg, pendingRequest]);

  const handleSend = async () => {
    if (isSubmitting) return;
    if (!amount) {
      toast.error('Amount is required');
      return;
    }
    if (!utr || !utr.trim()) {
      toast.error('UTR Number is required');
      return;
    }

    setIsSubmitting(true);
    try {
      // Perform live synchronous duplicate check before submitting
      const liveMatches = await checkDuplicateUtr(utr);

      if (liveMatches.length > 0 || duplicateMatches.length > 0) {
        setShowDuplicateTrail(true);
        toast.error("This UTR number has already been used for another pending or successful transaction.");
        setIsSubmitting(false);
        return;
      }

      if (editingMsg) {
        if (!formNo || !formNo.trim()) {
          toast.error("Form number (e.g. D021) is missing for this deposit transaction. Unable to submit edit.");
          setIsSubmitting(false);
          return;
        }
        // Return edit payload
        await onSend(null, { amount, utr, receivedFrom, receivedIn, description, oldAmount, txId, formNo });
        return;
      }

      let templateText = templates[selectedTemplateIdx]?.text || 
        `✅ आपके ID में {amount} Coins सफलतापूर्वक जमा कर दिए गए हैं। कृपया अपनी ID को Refresh करके चेक कर लें。\nUTR: {utr}\n✅ {amount} Coins have been successfully deposited into your ID. Please refresh your ID and check.`;

      let formatted = templateText
        .replace(/{amount}/g, amount)
        .replace(/{username}/g, customer?.name || 'Customer')
        .replace(/{utr}/g, utr)
        .replace(/{bank}/g, receivedIn || '')
        .replace(/{reference}/g, description || '')
        .replace(/{receivedFrom}/g, receivedFrom || '')
        .replace(/{receivedIn}/g, receivedIn || '');

      if (amount) {
        formatted = formatted
          .replace(/में\s+Coins/g, `में ${amount} Coins`)
          .replace(/✅\s+Coins\s+have\s+been/gi, `✅ ${amount} Coins have been`);
      }

      await onSend(formatted, { amount, utr, receivedFrom, receivedIn, description });
    } catch (err) {
      console.error('Failed to process deposit:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Custom Edit Deposit layout (2 Columns: old values vs new editable values)
  if (editingMsg) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px] overflow-y-auto">
        <div className="bg-wa-panel rounded-xl shadow-2xl max-w-2xl w-full border border-wa-border overflow-hidden animate-fade-in animate-duration-200 my-auto text-wa-text-primary">
          {/* Header */}
          <div className="bg-blue-600 px-4 py-3 flex items-center justify-between text-white font-semibold">
            <div className="flex items-center gap-4">
              <span>✏️</span>
              <span>EDIT DEPOSIT</span>
              {editHistory.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-full font-bold transition-all"
                >
                  {editHistory.length} previous edits
                </button>
              )}
            </div>
            <button onClick={onClose} disabled={isSubmitting} className="p-1 hover:bg-white/10 rounded-full text-white">
              <X size={18} weight="bold" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {isLoadingData ? (
              <div className="flex items-center justify-center py-8 text-blue-600 text-sm font-medium gap-2">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <circle className="opacity-75" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="30 60"></circle>
                </svg>
                <span>Loading deposit details...</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Old Values (Read-only) */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">पुरानी VALUE</h4>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">Amount (₹)</label>
                    <input
                      type="text"
                      value={oldAmount || '(empty)'}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-wa-hover text-wa-text-primary text-sm focus:outline-none cursor-not-allowed border-wa-border"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">UTR Number</label>
                    <input
                      type="text"
                      value={oldUtr || '(empty)'}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-wa-hover text-wa-text-primary text-sm focus:outline-none cursor-not-allowed border-wa-border"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">Description</label>
                    <input
                      type="text"
                      value={oldDescription || '(empty)'}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-wa-hover text-wa-text-primary text-sm focus:outline-none cursor-not-allowed border-wa-border"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">Bank Account</label>
                    <input
                      type="text"
                      value={oldReceivedIn || '(empty)'}
                      readOnly
                      className="w-full px-3 py-2 border rounded-lg bg-wa-hover text-wa-text-primary text-sm focus:outline-none cursor-not-allowed border-wa-border"
                    />
                  </div>
                </div>

                {/* Right Column - New Values (Editable) */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wider">नई VALUE</h4>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">Amount (₹)</label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border rounded-lg bg-wa-panel border-wa-border text-wa-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">UTR Number</label>
                    <input
                      type="text"
                      value={utr}
                      readOnly
                      disabled
                      className="w-full px-3 py-2 border rounded-lg bg-wa-hover text-wa-text-secondary text-sm focus:outline-none cursor-not-allowed border-wa-border"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">Description</label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border rounded-lg bg-wa-panel border-wa-border text-wa-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-wa-text-secondary mb-1">Bank Account</label>
                    <input
                      type="text"
                      value={receivedIn}
                      onChange={(e) => setReceivedIn(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border rounded-lg bg-wa-panel border-wa-border text-wa-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-wa-border pt-4 mt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-wa-border rounded-lg text-wa-text-primary hover:bg-wa-hover font-semibold text-sm transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={duplicateMatches.length > 0 || isSubmitting || isLoadingData}
                className={`px-4 py-2 text-white rounded-lg font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 ${
                  (duplicateMatches.length > 0 || isSubmitting || isLoadingData)
                    ? 'bg-blue-400 opacity-50 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <circle className="opacity-75" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="30 60"></circle>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Duplicate UTR Trail Modal inside Edit mode */}
        {showDuplicateTrail && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[150] p-4 backdrop-blur-[2px]">
            <div className="bg-wa-panel rounded-xl shadow-2xl max-w-lg w-full border border-wa-border overflow-hidden text-wa-text-primary animate-fade-in">
              {/* Header */}
              <div className="bg-[#ea0038] px-4 py-3 flex items-center justify-between text-white font-bold">
                <div className="flex items-center gap-2">
                  <span>⚠️</span>
                  <span>Duplicate UTR Trail</span>
                  <span className="text-[11px] bg-white/20 px-2 py-0.5 rounded font-normal font-mono">{utr}</span>
                </div>
                <button onClick={() => setShowDuplicateTrail(false)} className="text-white hover:opacity-80">
                  <X size={18} weight="bold" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[300px] overflow-y-auto">
                {duplicateMatches.map((m, idx) => (
                  <div key={m.id || idx} className="bg-[#e6f4ea] border border-[#a3cfbb] p-4 rounded-lg text-xs flex flex-col gap-2 relative">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1 text-[#0f5132] font-bold">
                        <span>📠</span>
                        <span>DEPOSIT</span>
                        <span className="text-[10px] bg-[#d1e7dd] text-[#0f5132] px-1.5 py-0.5 rounded border border-[#a3cfbb] font-bold uppercase ml-1">
                          DONE
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          toast.info("Navigating to chat...");
                          setShowDuplicateTrail(false);
                          onClose();
                        }}
                        className="border border-blue-500 text-blue-600 hover:bg-blue-50 rounded px-2.5 py-1 text-[11px] font-semibold transition-all flex items-center gap-1"
                      >
                        <span>⮵</span>
                        <span>Jump to chat</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-[#212529]">
                      <div className="space-y-1">
                        <div>
                          <span className="text-gray-500">Client:</span> <strong className="text-black">{m.username}</strong>
                        </div>
                        <div>
                          <span className="text-gray-500">When:</span> <span className="text-black font-medium">{formatTrailDate(m.created_at)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">By:</span> <span className="text-black font-medium">{m.creator_name || 'Admin'}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end">
                        <div className="text-right">
                          <span className="text-gray-500 text-[11px] block">Amount</span>
                          <strong className="text-[#0f5132] text-lg font-bold">₹{m.amount}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 bg-wa-hover border-t border-wa-border flex justify-between items-center">
                <span className="text-xs text-gray-500 font-light">Total matches: {duplicateMatches.length}</span>
                <button
                  type="button"
                  onClick={() => setShowDuplicateTrail(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-bold transition-all active:scale-[0.98]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit History Modal inside Edit mode */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[150] p-4 backdrop-blur-[2px]">
            <div className="bg-wa-panel rounded-xl shadow-2xl max-w-md w-full border border-wa-border overflow-hidden text-wa-text-primary animate-fade-in">
              <div className="bg-blue-600 px-4 py-3 flex items-center justify-between text-white font-bold">
                <span>Edit History - {editHistory.length}</span>
                <button onClick={() => setShowHistoryModal(false)} className="text-white hover:opacity-80">
                  <X size={18} weight="bold" />
                </button>
              </div>
              <div className="p-5 space-y-3 max-h-[300px] overflow-y-auto">
                {editHistory.map((h, idx) => (
                  <div key={h.id || idx} className="border border-wa-border bg-wa-hover p-3 rounded-lg space-y-1.5 text-xs">
                    <div className="font-semibold text-wa-text-primary">
                      Edit #{editHistory.length - idx} · {new Date(h.created_at).toLocaleString()} · by {h.changed_by_name} ({h.changed_by_role})
                    </div>
                    <div className="grid grid-cols-3 gap-2 border-t border-wa-border pt-1.5 text-[11px]">
                      <span className="font-medium text-wa-text-secondary capitalize">Field: {h.field_name}</span>
                      <span className="text-red-500">Before: {h.before_value}</span>
                      <span className="text-green-600 font-bold">After: {h.after_value}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 bg-wa-hover border-t border-wa-border flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-bold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Standard Create / Approve Deposit layout
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-wa-panel rounded-xl max-w-md w-full p-6 shadow-2xl border border-wa-border text-wa-text-primary relative animate-fade-in animate-duration-200">
        <h3 className="text-lg font-bold text-blue-600 mb-4 uppercase tracking-wide">
          {pendingRequest ? `Approve Deposit (${pendingRequest.form_id})` : 'Create and Deposit'}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Amount *</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. 5000"
              className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">UTR Number *</label>
            <input
              type="text"
              value={utr}
              onChange={(e) => setUtr(e.target.value)}
              disabled={isSubmitting}
              onBlur={(e) => {
                checkDuplicateUtr(e.target.value);
              }}
              placeholder="Enter UTR Number"
              className={`w-full px-3 py-2 border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none ${
                duplicateMatches.length > 0
                  ? 'border-red-500 ring-1 ring-red-500 focus:ring-2 focus:ring-red-500'
                  : 'border-wa-border focus:ring-2 focus:ring-blue-500'
              }`}
            />
            {duplicateMatches.length > 0 && (
              <div className="bg-[#fde8e8] border border-[#f8b4b4] rounded-lg p-2 mt-1.5 text-xs text-[#9b1c1c] flex items-center justify-between font-medium animate-fade-in">
                <div className="flex items-center gap-1.5">
                  <span>⚠️</span>
                  <span>UTR already used on {duplicateMatches.length} entry(s)</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDuplicateTrail(true)}
                  className="text-[#9b1c1c] hover:underline font-bold cursor-pointer ml-1"
                >
                  view trail —
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Received From (customer bank / UPI) (optional)</label>
            <input
              type="text"
              value={receivedFrom}
              onChange={(e) => setReceivedFrom(e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. SBI ****5678 or customer@ybl"
              className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Received In (our bank / UPI) (optional)</label>
            <input
              type="text"
              value={receivedIn}
              onChange={(e) => setReceivedIn(e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. HDFC ****1234 or our-upi@paytm"
              className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. UPI transfer for weekend bonus"
              rows={2}
              className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {!pendingRequest && templates.length > 1 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Select Template</label>
              <select
                value={selectedTemplateIdx}
                onChange={(e) => setSelectedTemplateIdx(Number(e.target.value))}
                disabled={isSubmitting}
                className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none"
              >
                {templates.map((t, idx) => (
                  <option key={idx} value={idx}>
                    Template {idx + 1} ({t.lang || 'Plain'})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 border border-wa-border rounded-lg hover:bg-wa-hover transition-colors font-medium text-sm text-wa-text-primary disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={duplicateMatches.length > 0 || isSubmitting}
            className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors font-semibold text-sm shadow-md flex items-center justify-center gap-2 ${
              (duplicateMatches.length > 0 || isSubmitting)
                ? 'bg-blue-400 opacity-50 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <circle className="opacity-75" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeDasharray="30 60"></circle>
                </svg>
                <span>{pendingRequest ? 'Approving...' : 'Sending...'}</span>
              </>
            ) : (
              pendingRequest ? 'Approve & Send' : 'Create & Approve'
            )}
          </button>
        </div>

        {/* Duplicate UTR Trail Modal inside Standard mode */}
        {showDuplicateTrail && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[150] p-4 backdrop-blur-[2px]">
            <div className="bg-wa-panel rounded-xl shadow-2xl max-w-lg w-full border border-wa-border overflow-hidden text-wa-text-primary animate-fade-in">
              {/* Header */}
              <div className="bg-[#ea0038] px-4 py-3 flex items-center justify-between text-white font-bold">
                <div className="flex items-center gap-2">
                  <span>⚠️</span>
                  <span>Duplicate UTR Trail</span>
                  <span className="text-[11px] bg-white/20 px-2 py-0.5 rounded font-normal font-mono">{utr}</span>
                </div>
                <button onClick={() => setShowDuplicateTrail(false)} className="text-white hover:opacity-80">
                  <X size={18} weight="bold" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4 max-h-[300px] overflow-y-auto">
                {duplicateMatches.map((m, idx) => (
                  <div key={m.id || idx} className="bg-[#e6f4ea] border border-[#a3cfbb] p-4 rounded-lg text-xs flex flex-col gap-2 relative">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-1 text-[#0f5132] font-bold">
                        <span>📠</span>
                        <span>DEPOSIT</span>
                        <span className="text-[10px] bg-[#d1e7dd] text-[#0f5132] px-1.5 py-0.5 rounded border border-[#a3cfbb] font-bold uppercase ml-1">
                          DONE
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          toast.info("Navigating to chat...");
                          setShowDuplicateTrail(false);
                          onClose();
                        }}
                        className="border border-blue-500 text-blue-600 hover:bg-blue-50 rounded px-2.5 py-1 text-[11px] font-semibold transition-all flex items-center gap-1"
                      >
                        <span>⮵</span>
                        <span>Jump to chat</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-[#212529]">
                      <div className="space-y-1">
                        <div>
                          <span className="text-gray-500">Client:</span> <strong className="text-black">{m.username}</strong>
                        </div>
                        <div>
                          <span className="text-gray-500">When:</span> <span className="text-black font-medium">{formatTrailDate(m.created_at)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">By:</span> <span className="text-black font-medium">{m.creator_name || 'Admin'}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end">
                        <div className="text-right">
                          <span className="text-gray-500 text-[11px] block">Amount</span>
                          <strong className="text-[#0f5132] text-lg font-bold">₹{m.amount}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 bg-wa-hover border-t border-wa-border flex justify-between items-center">
                <span className="text-xs text-gray-500 font-light">Total matches: {duplicateMatches.length}</span>
                <button
                  type="button"
                  onClick={() => setShowDuplicateTrail(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-xs font-bold transition-all active:scale-[0.98]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// CoinCutModal Component
const CoinCutModal = ({ isOpen, onClose, customer, roomId, editingMsg, pendingRequest, onSend }) => {
  const [amount, setAmount] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [upiId, setUpiId] = useState('');
  const [description, setDescription] = useState('');
  const [paidFrom, setPaidFrom] = useState('');
  const [utr, setUtr] = useState('');
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateIdx, setSelectedTemplateIdx] = useState(0);
  const [cooldownMinutes, setCooldownMinutes] = useState(0);
  const [hasSavedBank, setHasSavedBank] = useState(false);
  const [showBankFields, setShowBankFields] = useState(false);

  useEffect(() => {
    if (isOpen && customer) {
      setShowBankFields(false);
      if (editingMsg) {
        const text = editingMsg.text || '';
        
        const amountMatch = text.match(/Coins ₹\s*([0-9,]+)/i);
        if (amountMatch) {
          setAmount(amountMatch[1].replace(/,/g, ''));
        }
        
        const nameMatch = text.match(/Name:\s*(.*)/i);
        if (nameMatch) setAccountHolderName(nameMatch[1].trim());
        
        const acMatch = text.match(/A\/c:\s*(.*)/i) || text.match(/Acc:\s*(.*)/i) || text.match(/Account:\s*(.*)/i);
        if (acMatch) setBankAccountNumber(acMatch[1].trim());
        
        const ifscMatch = text.match(/IFSC:\s*(.*)/i);
        if (ifscMatch) setIfscCode(ifscMatch[1].trim());
        
        const bankMatch = text.match(/Bank:\s*(.*)/i);
        if (bankMatch) setBankName(bankMatch[1].trim());
        
        const upiMatch = text.match(/UPI ID:\s*(.*)/i) || text.match(/UPI:\s*(.*)/i);
        if (upiMatch) setUpiId(upiMatch[1].trim());

        const paidFromMatch = text.match(/Paid From:\s*([^\n]+)/i);
        if (paidFromMatch && !paidFromMatch[1].toLowerCase().includes('iss bank account')) setPaidFrom(paidFromMatch[1].trim());

        const utrMatch = text.match(/UTR:\s*(.*)/i);
        if (utrMatch) setUtr(utrMatch[1].trim());

        if (editingMsg.amount) setAmount(editingMsg.amount.toString());
        if (editingMsg.utr) setUtr(editingMsg.utr);
        if (editingMsg.paid_from) setPaidFrom(editingMsg.paid_from);
        
        setDescription(editingMsg.description || '');
        setCooldownMinutes(0);
      } else {
        const loadBankDetails = async () => {
          try {
            if (pendingRequest) {
              // Do NOT return early — always fetch latest Client Bank Detail from API below
            }

            if (customer?.id) {
              try {
                const cbdRes = await axios.get(`${API}/client-bank-details/${customer.id}`);
                // bank_details already merges client_bank over saved_bank on the backend
                const clientBd = cbdRes.data?.client_bank_detail;
                const bestBd = cbdRes.data?.bank_details;
                const fallbackBd = cbdRes.data?.saved_bank_detail;

                const bd = clientBd || bestBd || fallbackBd;
                if (bd) {
                  // Always use client_bank_detail fields if available, else fall back to bestBd
                  const holder = clientBd?.account_holder_name || bestBd?.account_holder_name || fallbackBd?.account_holder_name || '';
                  const acct = clientBd?.bank_account_number || bestBd?.bank_account_number || fallbackBd?.bank_account_number || '';
                  const ifsc = clientBd?.ifsc_code || bestBd?.ifsc_code || fallbackBd?.ifsc_code || '';
                  const bank = clientBd?.bank_name || bestBd?.bank_name || fallbackBd?.bank_name || '';
                  const upi = clientBd?.upi_id || bestBd?.upi_id || fallbackBd?.upi_id || '';
                  
                  setAccountHolderName(holder);
                  setBankAccountNumber(acct);
                  setIfscCode(ifsc);
                  setBankName(bank);
                  setUpiId(upi);

                  if (holder || acct || upi) {
                    setHasSavedBank(true);
                  }
                  return;
                }
              } catch (cbdErr) {
                console.error("Failed to fetch client-bank-details for coin cut:", cbdErr);
              }
            }

            const res = await axios.get(`${API}/clients/registered`);
            const client = res.data.find(c => c.id === customer.id || c.phone === customer.phone);
            if (client) {
              setAccountHolderName(client.account_holder_name || '');
              setBankAccountNumber(client.bank_account_number || '');
              setIfscCode(client.ifsc_code || '');
              setBankName(client.bank_name || '');
              setUpiId(client.upi_id || '');
              if (client.account_holder_name || client.bank_account_number || client.upi_id) {
                setHasSavedBank(true);
              }
            }
          } catch (e) {
            console.error(e);
          }
        };

        const checkCooldown = async () => {
          try {
            if (!roomId) return;
            const res = await axios.get(`${API}/chat/room/${roomId}/pending-count?type=withdrawal`);
            setCooldownMinutes(res.data.cooldown_minutes || 0);
          } catch (e) {
            console.error(e);
          }
        };

        loadBankDetails();
        if (pendingRequest) {
          setCooldownMinutes(0);
          setAmount('');
          setDescription(pendingRequest.description || '');
        } else {
          checkCooldown();
          setAmount('');
          setDescription('');
        }
        setPaidFrom('');
        setUtr('');
      }

      const loadTemplates = async () => {
        try {
          const seg = customer?.mb_setup || 'ALL MB';
          const res = await axios.get(`${API}/mb-config/bot-settings?segment=${seg}`);
          const raw = res.data.template || '';
          if (raw.trim().startsWith('{')) {
            const parsed = JSON.parse(raw);
            setTemplates(parsed.withdraw_templates || []);
          } else {
            setTemplates(raw ? [{ text: raw }] : []);
          }
        } catch (e) {
          console.error(e);
        }
      };

      loadTemplates();
    }
  }, [isOpen, customer, roomId, editingMsg, pendingRequest]);

  const handlePasteDetails = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      
      const lines = text.split('\n');
      lines.forEach(line => {
        const lower = line.toLowerCase();
        if (lower.includes('holder') || lower.includes('name')) {
          setAccountHolderName(line.split(':')[1]?.trim() || line.split('-')[1]?.trim() || '');
        } else if (lower.includes('a/c') || lower.includes('acc') || lower.includes('account')) {
          setBankAccountNumber(line.split(':')[1]?.trim() || line.split('-')[1]?.trim() || '');
        } else if (lower.includes('ifsc')) {
          setIfscCode(line.split(':')[1]?.trim() || line.split('-')[1]?.trim() || '');
        } else if (lower.includes('bank')) {
          setBankName(line.split(':')[1]?.trim() || line.split('-')[1]?.trim() || '');
        } else if (lower.includes('upi')) {
          setUpiId(line.split(':')[1]?.trim() || line.split('-')[1]?.trim() || '');
        }
      });
      toast.success('Pasted details from clipboard');
    } catch {
      toast.error('Could not read from clipboard');
    }
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSend = async () => {
    if (submitting) return;
    if (!pendingRequest && !editingMsg && cooldownMinutes > 0) {
      toast.error("Please wait 30 min for request new Withdrawal");
      return;
    }
    if (!amount) {
      toast.error('Amount is required');
      return;
    }

    const hasBank = Boolean(
      accountHolderName?.trim() &&
      bankAccountNumber?.trim() &&
      ifscCode?.trim() &&
      bankName?.trim()
    );
    const hasUpi = Boolean(upiId?.trim());
    const hasAutoSaved = Boolean(hasSavedBank && !showBankFields && (bankAccountNumber?.trim() || upiId?.trim()));

    if (!hasAutoSaved && !hasBank && !hasUpi) {
      toast.error("Please fill bank account details or UPI ID");
      return;
    }

    setSubmitting(true);
    try {
      let templateText = templates[selectedTemplateIdx]?.text || 
        `⚠️ Aapke {amount} coin cut kar liye gaye hain.\n25-30 minute ke andar aapka withdraw ho jayega.\n\nA/c Holder: {account_holder_name}\nBank A/c: {bank_account_number}\nIFSC: {ifsc}\nBank Name: {bank_name}\nUPI: {upi_id}\n\n🕒 Agle 10 minute mein aap bank detail change karva sakte hain.\n10 minute baad hamari zimmedari nahi hogi — isi account mein withdraw ho jayega.`;

      let formatted = templateText
        .replace(/{amount}/g, amount)
        .replace(/{username}/g, customer?.name || 'Customer')
        .replace(/{reference}/g, description || '')
        .replace(/{account_holder_name}/gi, accountHolderName || '-')
        .replace(/{bank_account_number}/gi, bankAccountNumber || '-')
        .replace(/{ifsc}/gi, ifscCode || '-')
        .replace(/{bank_name}/gi, bankName || '-')
        .replace(/{upi_id}/gi, upiId || '-');

      if (!formatted.includes('A/c Holder:') && !formatted.includes('Bank A/c:')) {
        const bankBlock = [
          `A/c Holder: ${accountHolderName || '-'}`,
          `Bank A/c: ${bankAccountNumber || '-'}`,
          `IFSC: ${ifscCode || '-'}`,
          `Bank Name: ${bankName || '-'}`,
          `UPI: ${upiId || '-'}`
        ].join('\n');

        if (formatted.includes('🕒 Agle 10 minute')) {
          formatted = formatted.replace('🕒 Agle 10 minute', `${bankBlock}\n\n🕒 Agle 10 minute`);
        } else if (formatted.includes('Agle 10 minute')) {
          formatted = formatted.replace('Agle 10 minute', `${bankBlock}\n\nAgle 10 minute`);
        } else {
          formatted = `${formatted}\n\n${bankBlock}`;
        }
      }

      // Auto-save edited bank details to client-bank-details so backend stays in sync
      if (customer?.id && (bankAccountNumber || upiId || accountHolderName)) {
        try {
          await axios.post(`${API}/client-bank-details`, {
            customer_id: customer.id,
            bank_name: bankName,
            account_holder_name: accountHolderName,
            bank_account_number: bankAccountNumber,
            ifsc_code: ifscCode,
            upi_id: upiId
          });
        } catch (e) {
          console.error("Failed to auto-save bank details from coin cut modal:", e);
        }
      }

      let paid_to = '';
      if (bankName || bankAccountNumber) {
        paid_to = [bankName, bankAccountNumber].filter(Boolean).join(' ');
      } else if (upiId) {
        paid_to = upiId;
      }

      await onSend(formatted, {
        amount,
        utr,
        paid_from: paidFrom,
        paid_to,
        description,
        bank_name: bankName,
        bank_account_number: bankAccountNumber,
        account_holder_name: accountHolderName,
        ifsc_code: ifscCode,
        upi_id: upiId
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-wa-panel rounded-xl max-w-md w-full p-6 shadow-2xl border border-wa-border text-wa-text-primary overflow-y-auto max-h-[90vh]">
        {/* Cooldown Banner */}
        {!pendingRequest && !editingMsg && cooldownMinutes > 0 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 mb-4 text-sm font-medium">
            <span className="text-lg">🔴</span>
            <span>30-min cooldown active. Try again after {cooldownMinutes} minute(s).</span>
          </div>
        )}

        <h3 className="text-lg font-bold text-orange-500 mb-4">{pendingRequest ? 'COIN CUT AMOUNT' : 'Create and cut'}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Amount (₹) *</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 7800"
              className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Saved bank indicator */}
          {hasSavedBank && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <span className="text-green-600 text-xs">✓</span>
              <span className="text-green-700 text-xs flex-1">
                Client bank saved - will be used automatically for this coin cut.
                <br />{accountHolderName && `${accountHolderName}`}{bankAccountNumber && `, A/c: ${bankAccountNumber}`}
              </span>
              <button
                type="button"
                onClick={() => setShowBankFields(prev => !prev)}
                className="px-2 py-0.5 bg-orange-500 hover:bg-orange-600 text-white rounded text-[10px] font-bold"
              >
                CHANGE
              </button>
            </div>
          )}

          {/* Client bank details inputs - shown if client has no saved bank OR if CHANGE button is clicked */}
          {(!hasSavedBank || showBankFields) && (
            <div className="border border-dashed border-wa-border rounded-lg p-3 bg-wa-hover/30 space-y-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-wa-text-secondary uppercase">CLIENT BANK DETAILS</span>
                <button
                  type="button"
                  onClick={handlePasteDetails}
                  className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white rounded text-[10px] font-bold"
                >
                  PASTE FROM CLIPBOARD
                </button>
              </div>
              
              <div className="space-y-2">
                <input
                  type="text"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  placeholder="A/c Holder Name"
                  className="w-full px-2 py-1 text-sm border border-wa-border rounded bg-wa-hover text-wa-text-primary"
                />
                <input
                  type="text"
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  placeholder="Bank Account Number"
                  className="w-full px-2 py-1 text-sm border border-wa-border rounded bg-wa-hover text-wa-text-primary"
                />
                <input
                  type="text"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  placeholder="IFSC Code"
                  className="w-full px-2 py-1 text-sm border border-wa-border rounded bg-wa-hover text-wa-text-primary"
                />
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="Bank Name"
                  className="w-full px-2 py-1 text-sm border border-wa-border rounded bg-wa-hover text-wa-text-primary"
                />
                <input
                  type="text"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="UPI ID"
                  className="w-full px-2 py-1 text-sm border border-wa-border rounded bg-wa-hover text-wa-text-primary"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Reference / Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. withdrawal ref"
              className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {templates.length > 1 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-wa-text-secondary mb-1">Select Template</label>
              <select
                value={selectedTemplateIdx}
                onChange={(e) => setSelectedTemplateIdx(Number(e.target.value))}
                className="w-full px-3 py-2 border border-wa-border rounded-lg bg-wa-hover text-wa-text-primary focus:outline-none"
              >
                {templates.map((t, idx) => (
                  <option key={idx} value={idx}>
                    Template {idx + 1} ({t.lang || 'Plain'})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-wa-border rounded-lg hover:bg-wa-hover transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={submitting || (!pendingRequest && !editingMsg && cooldownMinutes > 0)}
            className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-semibold text-sm shadow-md flex items-center justify-center gap-2"
          >
            {submitting && <CircleNotch size={16} className="animate-spin" />}
            {submitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Pending Deposit Requests Modal Component
const PendingRequestsModal = ({ isOpen, onClose, roomId, onApprove, onCancel }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRequests = async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chat/room/${roomId}/pending-deposits`);
      setRequests(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load pending requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRequests();
    }
  }, [isOpen, roomId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-[2px]">
      <div className="bg-wa-panel rounded-xl shadow-2xl max-w-sm w-full border border-wa-border overflow-hidden animate-fade-in animate-duration-200 text-wa-text-primary">
        {/* Header */}
        <div className="bg-[#1b5e20] px-4 py-3 flex items-center justify-between text-white font-semibold shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-lg">💰</span>
            <span>Pending Deposit Requests</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full text-white">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="bg-amber-50 text-[10px] text-amber-800 px-4 py-1.5 border-b border-amber-100 font-medium">
          <span>Pick one — Approve to send money, or Cancel with a reason. Drag the top bar to reposition.</span>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[300px] overflow-y-auto space-y-3">
          {loading ? (
            <p className="text-center text-sm text-wa-text-secondary">Loading...</p>
          ) : requests.length === 0 ? (
            <p className="text-center text-sm text-wa-text-secondary py-4">No pending deposit requests.</p>
          ) : (
            requests.map((req) => (
              <div key={req.id} className="border border-wa-border bg-wa-hover p-4 rounded-xl space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-sm text-wa-text-primary">{req.form_id}</span>
                  <span className="text-[10px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-semibold uppercase">
                    {req.type}
                  </span>
                </div>
                <div className="text-xs text-wa-text-secondary flex justify-between">
                  <span>Amount: <strong className="text-wa-text-primary">₹{req.amount}</strong></span>
                  <span>Date: {new Date(req.created_at).toLocaleString()}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onApprove(req)}
                    className="flex-1 py-2 bg-[#00a884] hover:bg-[#008f72] text-white font-bold rounded-lg text-xs transition-all active:scale-[0.98]"
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={() => onCancel(req)}
                    className="flex-1 py-2 bg-[#ea0038] hover:bg-[#c2002f] text-white font-bold rounded-lg text-xs transition-all active:scale-[0.98]"
                  >
                    ✗ Cancel
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HandlerChat;
